/**
 * SpacetimeDB v2.1 Network adapter for Warlords.
 *
 * Drop-in replacement for network.js. Exposes the same Network class API
 * that main.js expects, but uses SpacetimeDB subscriptions + reducers
 * instead of raw WebSocket messages.
 */

import { ARCHETYPES, ARMY_POINT_BUDGET, CULTURES, CUSTOM_UNITS, MAX_ARMY_SIZE } from "./game-config";
import {
  createProceduralMapRuntime,
  isProceduralManifest,
} from "./procedural-map";
import {
  DbConnection,
  type EventContext,
  type SubscriptionHandle,
} from "./module_bindings";
import {
  WorkerWsClient,
  isWorkerWsEnabled,
  type WorkerTickFrame,
  type WorkerMatchEnd,
} from "./worker-ws";
import {
  StandaloneBattleClient,
  isStandaloneBattleEnabled,
  type StandaloneMode,
} from "./standalone-battle";
import { WorkerTickBridge } from "./worker-tick-bridge";
import { parseVolleyVisual } from "./volley-visual";
import { artilleryField, parseArtilleryEvent, parseBatteryArtillery, parseProjectileArtillery } from "./artillery";

const COLUMN_FEEDBACK_EVENT_TYPES = new Set([
  "follow_changed", "follow_rejected", "column_deployment_blocked",
  "artillery_explosion", "artillery_fired", "artillery_rejected",
]);

class Network {
  isSpacetime = true;
  conn: DbConnection | null = null;
  onMessage: ((data: any) => void) | null = null;
  onOpen: (() => void) | null = null;
  onClose: (() => void) | null = null;

  private _identity: any = null;
  private _playerId: string = "";
  private _matchId: string | null = null;
  private _tickTimer: ReturnType<typeof setTimeout> | null = null;
  private _lastEmittedTick: number | null = null;
  private _stdbUrl: string = "";
  private _stdbDatabase: string = "";
  private _mapData: any = null;
  private _subscriptionReady = false;
  private _subscriptionError: string | null = null;
  private _mapCatalogLoaded = false;
  private _mapCatalogLoadPromise: Promise<
    { name: string; displayName: string }[]
  > | null = null;
  private _mapCatalog: { name: string; displayName: string }[] = [];
  private _loadedServerMapEntries = new Set<string>();
  private _baseSubscription: SubscriptionHandle | null = null;
  private _lobbySubscription: SubscriptionHandle | null = null;
  private _matchSubscription: SubscriptionHandle | null = null;
  private _matchSubscriptionId: string | null = null;
  private _serverMapSubscriptions = new Map<string, SubscriptionHandle>();
  private _pendingCreatedMatchIds = new Set<string>();
  private _awaitingCreatedMatch = false;
  private _openingConquestMatchId: string | null = null;
  private _finishedConquestMatchIds = new Set<string>();
  /** Match IDs we already emitted `match_ended` for from the embedded
   *  worker. Used to suppress the duplicate emit when the host's
   *  `submit_match_result` reducer eventually flips `match.phase` to
   *  `finished` and the matches.onUpdate subscription would otherwise
   *  fire a second results event (with stale/empty data because we've
   *  already torn down `_matchId`). */
  private _locallyFinishedMatchIds = new Set<string>();
  private _notifiedConquestMatchIds = new Set<string>();
  private _notifiedActiveMatchIds = new Set<string>();
  private _conquestEmitTimer: ReturnType<typeof setTimeout> | null = null;
  private _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private _connectSerial = 0;
  private _usingStoredToken = false;
  private _authBaseUrl: string = "";
  private _authToken: string | null = null;

  // Phase 5 (parallel) — battle-worker WebSocket state.
  private _workerWs: WorkerWsClient | null = null;
  private _workerWsMatchId: string | null = null;
  private _workerWsEndpointUrl: string | null = null;
  private _workerWsToken: string | null = null;
  // Phase 5b — standalone Tauri battle bridge. Mutually exclusive with
  // `_workerWs`: when running inside warlords-standalone the orchestrator
  // worker doesn't exist and the host runs the embedded worker locally.
  private _standaloneBattle: StandaloneBattleClient | null = null;
  private _standaloneBattleMatchId: string | null = null;
  // Phase 5d — caches that drive `_maybeStartStandaloneBattle`. Both
  // arrive asynchronously via separate row callbacks; we wait until both
  // are present (host election + start packet) before opening the bridge.
  private _matchHostByMatch: Map<string, string> = new Map();
  private _standaloneStartPacketByMatch: Map<string, string> = new Map();
  // Phase 5c: per-tick row cache produced from worker frames; the `_get*`
  // accessors below check `isActive(_matchId)` and read from here instead
  // of `this.conn.db.*` when the worker is the source of truth.
  private _workerBridge: WorkerTickBridge = new WorkerTickBridge();
  // Orders submitted before the worker WS handshake completes are buffered
  // here and flushed on `onOpen`. The module no longer ticks the battle, so
  // a SpacetimeDB reducer fallback would silently vanish.
  private _workerOrderQueue: { action: string; payload: any }[] = [];

  private _runtimePort(name: string, fallback: number): number {
    const value = new URLSearchParams(location.search).get(name);
    if (value === null) return fallback;
    if (!/^[1-9]\d{0,4}$/.test(value) || Number(value) > 65535) {
      throw new Error(`Invalid local development port: ${name}`);
    }
    return Number(value);
  }

  private _resolveAuthBaseUrl(): string {
    if (this._authBaseUrl) return this._authBaseUrl;
    const configured = (window as any).WARGAME_AUTH_URL;
    if (typeof configured === "string" && configured.trim()) {
      this._authBaseUrl = configured.replace(/\/$/, "");
      return this._authBaseUrl;
    }
    const host = location.hostname || "localhost";
    const proto = location.protocol === "https:" ? "https:" : "http:";
    this._authBaseUrl = `${proto}//${host}:${this._runtimePort("world_auth_port", 8090)}`;
    return this._authBaseUrl;
  }

  private async _authFetch(path: string, body: object): Promise<any> {
    const url = this._resolveAuthBaseUrl() + path;
    let resp: Response;
    try {
      resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    } catch (err: any) {
      throw new Error(`Auth service unreachable: ${err?.message || err}`);
    }
    let payload: any = null;
    try {
      payload = await resp.json();
    } catch {
      /* allow empty bodies */
    }
    if (!resp.ok) {
      const msg = payload?.error || `auth request failed (${resp.status})`;
      throw new Error(msg);
    }
    return payload || {};
  }

  connect(url: string) {
    const connectSerial = ++this._connectSerial;
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }

    // The original client passes a ws:// URL like ws://host:port/ws
    // SpacetimeDB runs on port 3000 by default.
    // The SDK expects http:// or https:// â€” it upgrades to ws internally.
    const configuredStdbUrl = (window as any).SPACETIMEDB_URL;
    const stdbHost = location.hostname || "localhost";
    const stdbUrl = configuredStdbUrl || `http://${stdbHost}:${this._runtimePort("world_stdb_port", 3000)}`;
    const stdbDatabase = new URLSearchParams(location.search).get("world_database")
      || (window as any).SPACETIMEDB_DATABASE || "warlords";
    this._stdbUrl = stdbUrl;
    this._stdbDatabase = stdbDatabase;
    this._subscriptionReady = false;
    this._subscriptionError = null;
    this._mapCatalogLoaded = false;
    this._mapCatalogLoadPromise = null;
    this._mapCatalog = [];
    this._loadedServerMapEntries = new Set<string>();
    this._baseSubscription = null;
    this._lobbySubscription = null;
    this._matchSubscription = null;
    this._matchSubscriptionId = null;
    this._serverMapSubscriptions = new Map<string, SubscriptionHandle>();
    this._pendingCreatedMatchIds.clear();
    this._awaitingCreatedMatch = false;

    const storedToken = localStorage.getItem("spacetimedb_token") || undefined;
    this._usingStoredToken = !!storedToken;

    let builder = DbConnection.builder()
      .withUri(stdbUrl)
      .withDatabaseName(stdbDatabase);

    // Only attach a token if we have a valid one; empty/stale tokens cause auth failures
    if (storedToken) {
      builder = builder.withToken(storedToken);
    }

    this.conn = builder
      .onConnect((conn: DbConnection, identity: any, token: string) => {
        if (connectSerial !== this._connectSerial) return;
        this._identity = identity;
        this._playerId = identity.toHexString();
        localStorage.setItem("spacetimedb_token", token);
        this._usingStoredToken = !!storedToken;

        // Set up table callbacks before subscribing
        this._setupCallbacks();

        // Subscribe to global tables and caller-specific views
        this._baseSubscription = conn
          .subscriptionBuilder()
          .onApplied(() => {
            if (connectSerial !== this._connectSerial) return;
            this._subscriptionReady = true;
            this._subscriptionError = null;
            const session = this._getSession();
            this._playerId = session?.playerId || this._playerId;
            // Re-present any stored auth JWT so the user stays logged in
            // across SpacetimeDB --delete-data wipes.
            void this._replayStoredAuthToken();
            // Emit welcome message that main.js expects
            this._emit({
              type: "welcome",
              player_id: this._playerId,
              authenticated: session?.authenticated ?? false,
              username: session?.username ?? null,
              tier: session?.tier ?? "free",
              archetypes: ARCHETYPES,
              cultures: CULTURES,
              custom_units: CUSTOM_UNITS,
              army_budget: ARMY_POINT_BUDGET,
              max_army_size: MAX_ARMY_SIZE,
            });
            if (this._matchId) {
              void this._restoreActiveMatchContext(this._matchId);
            } else {
              this._emitActiveMatchAvailable();
            }
            this._emitArenaState();
            if (this.onOpen) this.onOpen();
          })
          .onError((ctx: any) => {
            if (connectSerial !== this._connectSerial) return;
            const error = ctx?.event?.error ?? ctx?.error ?? ctx;
            this._subscriptionReady = false;
            this._subscriptionError = error?.message || String(error);
            const lowerError = this._subscriptionError.toLowerCase();
            const isProfileMaterializationError =
              lowerError.includes("materializing view") &&
              lowerError.includes("my_conquest_profiles");
            if (
              isProfileMaterializationError &&
              localStorage.getItem("spacetimedb_token") &&
              sessionStorage.getItem("warlords.profileViewRecoveryAttempted") !== "1"
            ) {
              sessionStorage.setItem("warlords.profileViewRecoveryAttempted", "1");
              localStorage.removeItem("spacetimedb_token");
              this._usingStoredToken = false;
              this._emit({
                type: "error",
                message: "Resetting stale campaign session and reconnecting...",
              });
              try {
                (this.conn as any)?.disconnect?.();
              } catch {
                /* noop */
              }
              this._scheduleReconnect(url, 250);
              return;
            }
            this._emit({
              type: "error",
              message:
                this._subscriptionError || "SpacetimeDB subscription failed",
            });
          })
          .subscribe([
            "SELECT * FROM my_session",
            "SELECT * FROM players",
            "SELECT * FROM my_armies",
            "SELECT * FROM my_army_units",
            "SELECT * FROM my_army_formations",
            "SELECT * FROM my_army_groups",
            "SELECT * FROM campaign_worlds",
            "SELECT * FROM campaign_cities",
            "SELECT * FROM campaign_armies",
            "SELECT * FROM campaign_battles",
            "SELECT * FROM campaign_events",
            "SELECT * FROM my_campaign_player",
            "SELECT * FROM my_campaign_resources",
            "SELECT * FROM my_campaign_recruitment",
            "SELECT * FROM my_campaign_army",
            "SELECT * FROM my_conquest_profiles_v_2",
            "SELECT * FROM my_conquest_custom_unit_templates",
            "SELECT * FROM my_conquest_saved_units",
            "SELECT * FROM my_conquest_territories",
            "SELECT * FROM my_conquest_queue",
            "SELECT * FROM my_match_meta",
            "SELECT * FROM conquest_lobby_timers",
            "SELECT * FROM my_conquest_challenges",
            "SELECT * FROM my_conquest_events",
            "SELECT * FROM map_data",
            "SELECT * FROM arena_matchmaking_queue",
            "SELECT * FROM arena_matchmaking_lobbies",
            "SELECT * FROM arena_matchmaking_lobby_players",
          ]);
      })
      .onDisconnect(() => {
        if (connectSerial !== this._connectSerial) return;
        this._subscriptionReady = false;
        this._subscriptionError = null;
        this._identity = null;
        this._mapCatalogLoaded = false;
        this._mapCatalogLoadPromise = null;
        this._mapCatalog = [];
        this._loadedServerMapEntries = new Set<string>();
        this._pendingCreatedMatchIds.clear();
        this._awaitingCreatedMatch = false;
        this._baseSubscription = null;
        this._lobbySubscription = null;
        this._matchSubscription = null;
        this._matchSubscriptionId = null;
        this._serverMapSubscriptions = new Map<string, SubscriptionHandle>();
        this._usingStoredToken = false;
        if (this.onClose) this.onClose();
        this._scheduleReconnect(url, 3000);
      })
      .onConnectError((_ctx: any, err: any) => {
        if (connectSerial !== this._connectSerial) return;
        this._subscriptionReady = false;
        this._subscriptionError = err?.message || String(err);
        this._identity = null;
        if (this._usingStoredToken && this._isStoredTokenAuthError(err)) {
          localStorage.removeItem("spacetimedb_token");
          this._usingStoredToken = false;
          this._scheduleReconnect(url, 250);
        } else {
          this._scheduleReconnect(url, 3000);
        }
        console.error("[Network] SpacetimeDB connect error:", err);
        this._emit({
          type: "error",
          message: `Failed to connect to ${stdbDatabase} at ${stdbUrl}`,
        });
      })
      .build();
  }

  private _isStoredTokenAuthError(err: any): boolean {
    const message = String(err?.message || err || "").toLowerCase();
    return (
      message.includes("invalid token") ||
      message.includes("bad token") ||
      message.includes("expired token") ||
      message.includes("token expired") ||
      message.includes("invalid auth") ||
      message.includes("authentication failed") ||
      message.includes("identity mismatch") ||
      message.includes("unauthorized")
    );
  }

  private _scheduleReconnect(url: string, delayMs: number) {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
    }
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      this.connect(url);
    }, delayMs);
  }

  private _setupCallbacks() {
    if (!this.conn) return;

    // Diagnostic: surface harvest reducer FAILURE events only (success is
    // implied by the resulting state changes; per-call success spam slows the
    // browser console at 10 Hz).
    try {
      const reducers = this.conn.reducers as any;
      if (typeof reducers?.onDesignateHarvestTile === "function") {
        reducers.onDesignateHarvestTile((ctx: any, ..._args: any[]) => {
          const status = ctx?.event?.status;
          if (status?.tag === "Failed") {
            console.error("[harvest] designateHarvestTile reducer FAILED:", status?.value, "args=", _args);
          }
        });
      }
      if (typeof reducers?.onUndesignateHarvestTile === "function") {
        reducers.onUndesignateHarvestTile((ctx: any, ..._args: any[]) => {
          const status = ctx?.event?.status;
          if (status?.tag === "Failed") {
            console.error("[harvest] undesignateHarvestTile FAILED:", status?.value, "args=", _args);
          }
        });
      }
      if (typeof reducers?.onCancelWallBuildProject === "function") {
        reducers.onCancelWallBuildProject((ctx: any, ..._args: any[]) => {
          const status = ctx?.event?.status;
          if (status?.tag === "Failed") {
            console.error("[harvest] cancelWallBuildProject FAILED:", status?.value, "args=", _args);
          }
        });
      }
    } catch (err) {
      console.warn("[harvest] failed to bind reducer-event diagnostics", err);
    }

    // Diagnostic: log match_economy rows on insert only (per-tick onUpdate
    // spam was causing console-driven main-thread slowdowns).
    try {
      const econTable = (this.conn.db as any).match_economy;
      if (econTable?.onInsert) {
        econTable.onInsert((_ctx: any, row: any) => {
          console.log("[harvest] match_economy onInsert", { matchId: row?.matchId, woodJson: row?.woodJson, harvestTilesJson: row?.harvestTilesJson });
        });
      }
    } catch (err) {
      console.warn("[harvest] failed to bind match_economy diagnostics", err);
    }

    // --- Match table updates (phase changes, tick) ---
    this.conn.db.matches.onUpdate(
      (_ctx: EventContext, oldRow: any, newRow: any) => {
        if (this._matchId && newRow.id === this._matchId) {
          // Phase change
          if (oldRow.phase !== newRow.phase) {
            const evt: any = {
              type: "phase_changed",
              phase: newRow.phase,
            };
            // Include map data when entering setup phase
            if (newRow.phase === "setup" && this._mapData) {
              evt.map = this._mapData;
            }
            // Include map data when entering running phase too
            if (newRow.phase === "running" && this._mapData) {
              evt.map = this._mapData;
            }
            this._emit(evt);

            // When entering running phase, emit initial tick so units appear
            if (newRow.phase === "running") {
              this._scheduleTick();
              // Phase 5c — standalones race to become host. The first
              // claim wins; everyone else gets a clean error and reads
              // the row to learn its role.
              if (isStandaloneBattleEnabled() && this._matchId) {
                const match = this.conn.db.matches.id.find(this._matchId);
                const isArena = match ? (match.mode === "arena") : false;
                if (!isArena) {
                  this.claimMatchHost(this._matchId);
                }
              }
            }

            // When match finishes, emit match_ended with results
            if (newRow.phase === "finished") {
              if (this._locallyFinishedMatchIds.has(newRow.id)) {
                this._locallyFinishedMatchIds.delete(newRow.id);
              } else {
                this._emitMatchEnded(newRow);
              }
            }
          }
        }
      },
    );

    this.conn.db.matches.onDelete((_ctx: EventContext, row: any) => {
      if (!this._matchId || row.id !== this._matchId) return;
      const mode = ((row as any)?.mode as string | null | undefined) || "skirmish";
      if (mode === "conquest") {
        this._finishedConquestMatchIds.add(row.id);
      }
      this._emit({
        type: "match_cancelled",
        match_id: row.id,
        mode,
        ruleset:
          ((row as any)?.ruleset as string | null | undefined) || "classical",
      });
      this._matchId = null;
      this._lastEmittedTick = null;
      this._clearMatchSubscription();
      if (mode === "conquest") {
        this._emitConquestState();
      }
    });

    // --- Match army updates (lobby state) ---
    const emitLobbyUpdate = () => {
      if (!this._matchId || !this.conn) return;
      const armies = this._getMatchArmies();
      this._emit({
        type: "army_updated",
        armies,
        conquest_lobby_timer: this._getConquestLobbyTimer(this._matchId),
      });

      // Lobby→setup countdown is handled client-side in main.js _updateLobbyCountdown().
      // Setup→running auto-start: when all players re-ready after deployment, host starts immediately.
      const match = this.conn.db.matches.id.find(this._matchId);
      if (
        match &&
        match.phase === "setup" &&
        match.hostPlayerId === this._playerId
      ) {
        const claimed = [];
        for (const a of this.conn.db.match_armies.iter()) {
          if (a.matchId === this._matchId && a.playerId) {
            claimed.push(a);
          }
        }
        if (claimed.length >= 2 && claimed.every((a: any) => a.ready)) {
          void (this.conn.reducers.startMatch({ matchId: this._matchId }) as any)
            ?.catch?.((error: any) => {
              this._emitReducerError(error, "Failed to start match");
            });
        }
      }
    };
    this.conn.db.match_armies.onInsert((_ctx: EventContext, _row: any) =>
      emitLobbyUpdate(),
    );
    this.conn.db.match_armies.onUpdate(
      (_ctx: EventContext, _old: any, _new: any) => emitLobbyUpdate(),
    );
    this.conn.db.match_armies.onDelete((_ctx: EventContext, _row: any) =>
      emitLobbyUpdate(),
    );
    const conquestTimers = (this.conn.db as any).conquest_lobby_timers;
    conquestTimers?.onInsert((_ctx: EventContext, row: any) => {
      if (row.matchId === this._matchId) {
        this._emit({ type: "conquest_lobby_timer", timer: this._mapConquestLobbyTimer(row) });
      }
    });
    conquestTimers?.onUpdate((_ctx: EventContext, _old: any, row: any) => {
      if (row.matchId === this._matchId) {
        this._emit({ type: "conquest_lobby_timer", timer: this._mapConquestLobbyTimer(row) });
      }
    });
    conquestTimers?.onDelete((_ctx: EventContext, row: any) => {
      if (row.matchId === this._matchId) {
        this._emit({ type: "conquest_lobby_timer", timer: null });
      }
    });

    // --- Typed match-state updates ---
    const scheduleTick = () => this._scheduleTick();
    this.conn.db.my_match_roads.onInsert(() => scheduleTick());
    this.conn.db.my_match_roads.onUpdate(() => scheduleTick());
    this.conn.db.my_match_roads.onDelete(() => scheduleTick());
    const handleMatchMetaUpdate = () => {
      scheduleTick();
      this._emitActiveMatchAvailable();
    };
    this.conn.db.my_match_meta.onInsert((_ctx: EventContext, _row: any) =>
      handleMatchMetaUpdate(),
    );
    this.conn.db.my_match_meta.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => handleMatchMetaUpdate(),
    );
    this.conn.db.my_match_meta.onDelete((_ctx: EventContext, _row: any) =>
      scheduleTick(),
    );
    this.conn.db.my_match_follow_waypoints.onInsert(
      (_ctx: EventContext, _row: any) => scheduleTick(),
    );
    this.conn.db.my_match_follow_waypoints.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => scheduleTick(),
    );
    this.conn.db.my_match_follow_waypoints.onDelete(
      (_ctx: EventContext, _row: any) => scheduleTick(),
    );
    this.conn.db.my_friendly_units.onInsert((_ctx: EventContext, _row: any) =>
      scheduleTick(),
    );
    this.conn.db.my_friendly_units.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => scheduleTick(),
    );
    this.conn.db.my_friendly_units.onDelete((_ctx: EventContext, _row: any) =>
      scheduleTick(),
    );
    this.conn.db.my_enemy_units.onInsert((_ctx: EventContext, _row: any) =>
      scheduleTick(),
    );
    this.conn.db.my_enemy_units.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => scheduleTick(),
    );
    this.conn.db.my_enemy_units.onDelete((_ctx: EventContext, _row: any) =>
      scheduleTick(),
    );
    this.conn.db.my_unit_skills.onInsert((_ctx: EventContext, _row: any) =>
      scheduleTick(),
    );
    this.conn.db.my_unit_skills.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => scheduleTick(),
    );
    this.conn.db.my_unit_skills.onDelete((_ctx: EventContext, _row: any) =>
      scheduleTick(),
    );
    this.conn.db.my_unit_order_queue.onInsert((_ctx: EventContext, _row: any) =>
      scheduleTick(),
    );
    this.conn.db.my_unit_order_queue.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => scheduleTick(),
    );
    this.conn.db.my_unit_order_queue.onDelete((_ctx: EventContext, _row: any) =>
      scheduleTick(),
    );
    this.conn.db.my_unit_path_waypoints.onInsert(
      (_ctx: EventContext, _row: any) => scheduleTick(),
    );
    this.conn.db.my_unit_path_waypoints.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => scheduleTick(),
    );
    this.conn.db.my_unit_path_waypoints.onDelete(
      (_ctx: EventContext, _row: any) => scheduleTick(),
    );
    this.conn.db.my_engagements.onInsert((_ctx: EventContext, _row: any) =>
      scheduleTick(),
    );
    this.conn.db.my_engagements.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => scheduleTick(),
    );
    this.conn.db.my_engagements.onDelete((_ctx: EventContext, _row: any) =>
      scheduleTick(),
    );
    this.conn.db.my_projectiles.onInsert((_ctx: EventContext, _row: any) =>
      scheduleTick(),
    );
    this.conn.db.my_projectiles.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => scheduleTick(),
    );
    this.conn.db.my_projectiles.onDelete((_ctx: EventContext, _row: any) =>
      scheduleTick(),
    );
    this.conn.db.my_messengers.onInsert((_ctx: EventContext, _row: any) =>
      scheduleTick(),
    );
    this.conn.db.my_messengers.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => scheduleTick(),
    );
    this.conn.db.my_messengers.onDelete((_ctx: EventContext, _row: any) =>
      scheduleTick(),
    );
    this.conn.db.match_capture_points.onInsert(
      (_ctx: EventContext, _row: any) => scheduleTick(),
    );
    this.conn.db.match_capture_points.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => scheduleTick(),
    );
    this.conn.db.match_capture_points.onDelete(
      (_ctx: EventContext, _row: any) => scheduleTick(),
    );
    this.conn.db.match_structure_state.onInsert(
      (_ctx: EventContext, _row: any) => scheduleTick(),
    );
    this.conn.db.match_structure_state.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => scheduleTick(),
    );
    this.conn.db.match_structure_state.onDelete(
      (_ctx: EventContext, _row: any) => scheduleTick(),
    );
    this.conn.db.match_camps.onInsert((_ctx: EventContext, _row: any) =>
      scheduleTick(),
    );
    this.conn.db.match_camps.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => scheduleTick(),
    );
    this.conn.db.match_camps.onDelete((_ctx: EventContext, _row: any) =>
      scheduleTick(),
    );
    this.conn.db.match_events.onInsert((_ctx: EventContext, _row: any) =>
      scheduleTick(),
    );
    this.conn.db.match_events.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => scheduleTick(),
    );
    this.conn.db.match_events.onDelete((_ctx: EventContext, _row: any) =>
      scheduleTick(),
    );
    this.conn.db.my_visibility_bitmap.onInsert(
      (_ctx: EventContext, _row: any) => scheduleTick(),
    );
    this.conn.db.my_visibility_bitmap.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => scheduleTick(),
    );
    this.conn.db.my_visibility_bitmap.onDelete(
      (_ctx: EventContext, _row: any) => scheduleTick(),
    );

    // Phase 5 (parallel) — battle-worker WebSocket endpoint published by the
    // orchestrator. When `USE_WORKER_WS` is enabled, opening / closing the
    // WebSocket is driven by these row events.
    this.conn.db.my_match_worker_endpoint.onInsert(
      (_ctx: EventContext, row: any) => this._onWorkerEndpointRow(row),
    );
    this.conn.db.my_match_worker_endpoint.onUpdate(
      (_ctx: EventContext, _old: any, row: any) =>
        this._onWorkerEndpointRow(row),
    );
    this.conn.db.my_match_worker_endpoint.onDelete(
      (_ctx: EventContext, _row: any) => this._closeWorkerWs("endpoint_removed"),
    );

    // Phase 5c — standalone host-election rows. We surface the role
    // (`host` / `client` / `none`) to whoever listens for
    // `standalone_battle_role` events so the lobby can pick the right
    // mode for `connectStandaloneBattle`.
    this.conn.db.match_host.onInsert((_ctx: EventContext, row: any) =>
      this._onMatchHostRow(row, "insert"),
    );
    this.conn.db.match_host.onUpdate(
      (_ctx: EventContext, _old: any, row: any) =>
        this._onMatchHostRow(row, "update"),
    );
    this.conn.db.match_host.onDelete((_ctx: EventContext, row: any) =>
      this._onMatchHostRow(row, "delete"),
    );

    // Phase 5d — start packet view rows. Cache the JSON so that
    // `_maybeStartStandaloneBattle` can spawn the embedded worker as
    // soon as both the host election row and the start packet have
    // arrived (order is not deterministic).
    this.conn.db.my_standalone_start_packet.onInsert(
      (_ctx: EventContext, row: any) => this._onStandaloneStartPacketRow(row, "upsert"),
    );
    this.conn.db.my_standalone_start_packet.onUpdate(
      (_ctx: EventContext, _old: any, row: any) =>
        this._onStandaloneStartPacketRow(row, "upsert"),
    );
    this.conn.db.my_standalone_start_packet.onDelete(
      (_ctx: EventContext, row: any) => this._onStandaloneStartPacketRow(row, "delete"),
    );

    // --- Secure per-player session updates ---
    const emitSessionUpdate = () => {
      const session = this._getSession();
      this._playerId = session?.playerId || this._playerId;
      this._emit({
        type: "authenticated",
        player_id: this._playerId,
        authenticated: session?.authenticated ?? false,
        username: session?.username ?? null,
        tier: session?.tier ?? "free",
      });
      this._emitActiveMatchAvailable();
    };
    this.conn.db.my_session.onInsert((_ctx: EventContext, _row: any) =>
      emitSessionUpdate(),
    );
    this.conn.db.my_session.onUpdate(
      (_ctx: EventContext, _old: any, _new: any) => emitSessionUpdate(),
    );
    this.conn.db.my_session.onDelete((_ctx: EventContext, _row: any) =>
      emitSessionUpdate(),
    );

    const emitSkirmishLobbyMatches = () => {
      this._emitMatchList();
      this._maybeEmitPendingCreatedMatch();
    };
    this.conn.db.skirmish_lobby_matches.onInsert(
      (_ctx: EventContext, _row: any) => emitSkirmishLobbyMatches(),
    );
    this.conn.db.skirmish_lobby_matches.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => emitSkirmishLobbyMatches(),
    );
    this.conn.db.skirmish_lobby_matches.onDelete(
      (_ctx: EventContext, _row: any) => emitSkirmishLobbyMatches(),
    );

    const emitCampaignState = () => this._emitCampaignState();
    this.conn.db.campaign_worlds.onInsert((_ctx: EventContext, _row: any) =>
      emitCampaignState(),
    );
    this.conn.db.campaign_worlds.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => emitCampaignState(),
    );
    this.conn.db.campaign_cities.onInsert((_ctx: EventContext, _row: any) =>
      emitCampaignState(),
    );
    this.conn.db.campaign_cities.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => emitCampaignState(),
    );
    this.conn.db.campaign_armies.onInsert((_ctx: EventContext, _row: any) =>
      emitCampaignState(),
    );
    this.conn.db.campaign_armies.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => emitCampaignState(),
    );
    this.conn.db.campaign_battles.onInsert((_ctx: EventContext, _row: any) =>
      emitCampaignState(),
    );
    this.conn.db.campaign_battles.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => emitCampaignState(),
    );
    this.conn.db.campaign_events.onInsert((_ctx: EventContext, row: any) => {
      this._emit({ type: "campaign_event", event: this._mapCampaignEvent(row) });
      emitCampaignState();
    });
    this.conn.db.my_campaign_player.onInsert((_ctx: EventContext, _row: any) =>
      emitCampaignState(),
    );
    this.conn.db.my_campaign_player.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => emitCampaignState(),
    );
    this.conn.db.my_campaign_resources.onInsert(
      (_ctx: EventContext, _row: any) => emitCampaignState(),
    );
    this.conn.db.my_campaign_resources.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => emitCampaignState(),
    );
    this.conn.db.my_campaign_recruitment.onInsert(
      (_ctx: EventContext, _row: any) => emitCampaignState(),
    );
    this.conn.db.my_campaign_recruitment.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => emitCampaignState(),
    );
    this.conn.db.my_campaign_recruitment.onDelete(
      (_ctx: EventContext, _row: any) => emitCampaignState(),
    );
    this.conn.db.my_campaign_army.onInsert((_ctx: EventContext, _row: any) =>
      emitCampaignState(),
    );
    this.conn.db.my_campaign_army.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => emitCampaignState(),
    );

    const emitConquestState = () => this._emitConquestState();
    const conquestProfilesTable = (this.conn.db as any).my_conquest_profiles_v2 ?? this.conn.db.my_conquest_profiles;
    conquestProfilesTable.onInsert(
      (_ctx: EventContext, _row: any) => emitConquestState(),
    );
    conquestProfilesTable.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => emitConquestState(),
    );
    (this.conn.db as any).my_conquest_custom_unit_templates?.onInsert(
      (_ctx: EventContext, _row: any) => emitConquestState(),
    );
    (this.conn.db as any).my_conquest_custom_unit_templates?.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => emitConquestState(),
    );
    (this.conn.db as any).my_conquest_custom_unit_templates?.onDelete(
      (_ctx: EventContext, _row: any) => emitConquestState(),
    );
    (this.conn.db as any).my_conquest_saved_units?.onInsert(
      (_ctx: EventContext, _row: any) => emitConquestState(),
    );
    (this.conn.db as any).my_conquest_saved_units?.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => emitConquestState(),
    );
    (this.conn.db as any).my_conquest_saved_units?.onDelete(
      (_ctx: EventContext, _row: any) => emitConquestState(),
    );
    this.conn.db.my_conquest_territories.onInsert(
      (_ctx: EventContext, _row: any) => emitConquestState(),
    );
    this.conn.db.my_conquest_territories.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => emitConquestState(),
    );
    this.conn.db.my_conquest_queue.onInsert(
      (_ctx: EventContext, _row: any) => emitConquestState(),
    );
    this.conn.db.my_conquest_queue.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => emitConquestState(),
    );
    this.conn.db.my_conquest_queue.onDelete(
      (_ctx: EventContext, _row: any) => emitConquestState(),
    );
    this.conn.db.my_conquest_challenges.onInsert(
      (_ctx: EventContext, _row: any) => emitConquestState(),
    );
    this.conn.db.my_conquest_challenges.onUpdate(
      (_ctx: EventContext, _old: any, _row: any) => emitConquestState(),
    );
    this.conn.db.my_conquest_events.onInsert((_ctx: EventContext, row: any) => {
      this._emit({ type: "conquest_event", event: this._mapConquestEvent(row) });
      emitConquestState();
    });

    const emitArenaState = () => this._emitArenaState();
    this.conn.db.arena_matchmaking_queue.onInsert((_ctx: EventContext, _row: any) => emitArenaState());
    this.conn.db.arena_matchmaking_queue.onUpdate((_ctx: EventContext, _old: any, _row: any) => emitArenaState());
    this.conn.db.arena_matchmaking_queue.onDelete((_ctx: EventContext, _row: any) => emitArenaState());

    this.conn.db.arena_matchmaking_lobbies.onInsert((_ctx: EventContext, _row: any) => emitArenaState());
    this.conn.db.arena_matchmaking_lobbies.onUpdate((_ctx: EventContext, _old: any, _row: any) => emitArenaState());
    this.conn.db.arena_matchmaking_lobbies.onDelete((_ctx: EventContext, _row: any) => emitArenaState());

    this.conn.db.arena_matchmaking_lobby_players.onInsert((_ctx: EventContext, _row: any) => emitArenaState());
    this.conn.db.arena_matchmaking_lobby_players.onUpdate((_ctx: EventContext, _old: any, _row: any) => emitArenaState());
    this.conn.db.arena_matchmaking_lobby_players.onDelete((_ctx: EventContext, _row: any) => emitArenaState());
  }

  // --- Tick batching ---

  /** True when a worker-WS or standalone battle bridge owns the live
   *  state for `matchId`. While owned, `_emitTickUpdate` must only emit
   *  ticks fed by the bridge — falling back to the SpacetimeDB `my_*`
   *  rows would snap units back to their setup-phase deployment positions
   *  because the module sim is disabled. */
  private _isBattleBridgeOwned(matchId: string | null): boolean {
    if (!matchId) return false;
    if (this._standaloneBattle && this._standaloneBattleMatchId === matchId) {
      return true;
    }
    if (this._workerWs && this._workerWsMatchId === matchId) {
      return true;
    }
    return false;
  }

  private _scheduleTick() {
    if (this._tickTimer) return;
    this._tickTimer = setTimeout(() => {
      this._tickTimer = null;
      this._emitTickUpdate();
    }, 100); // Spacetime ticks at 10 Hz; batch row callbacks to that cadence.
  }

  private _emitTickUpdate() {
    if (!this.conn || !this._matchId) {
      return;
    }

    const workerActive = this._workerBridge.isActive(this._matchId);
    const meta = this._getCurrentMatchMetaRow();
    const match = this.conn.db.matches.id.find(this._matchId);
    const tick = workerActive
      ? this._workerBridge.tick
      : (meta?.tick ?? ((match?.tick ?? 0) + (match?.startTickOffset ?? 0)));
    const phase = workerActive
      ? (this._workerBridge.phase || meta?.phase || match?.phase || "lobby")
      : (meta?.phase ?? match?.phase ?? "lobby");
    if (phase !== "running") {
      return;
    }
    // While a standalone (or worker-WS) battle owns this match, the bridge
    // is the only legitimate source of unit positions. Refuse to emit a
    // tick driven by stray SpacetimeDB row callbacks (which still hold the
    // initial deployment-zone positions and would teleport units back).
    if (!workerActive && this._isBattleBridgeOwned(this._matchId)) {
      return;
    }
    if (this._lastEmittedTick === tick) {
      return;
    }

    const state = this._getCurrentMatchState();
    if (!state || state.phase !== "running") {
      return;
    }
    this._lastEmittedTick = tick;

    const units = Array.isArray(state.units) ? state.units : [];
    const engagements = Array.isArray(state.engagements)
      ? state.engagements
      : [];
    const projectiles = Array.isArray(state.projectiles)
      ? state.projectiles
      : [];
    const messengers = Array.isArray(state.messengers) ? state.messengers : [];
    const events = Array.isArray(state.events) ? state.events : [];

    const follow: Record<number, any> = {};
    const myArmyIndex = this._getMyArmyIndex();
    if (state.follow && myArmyIndex >= 0) {
      follow[myArmyIndex] = {
        active: !!state.follow.active,
        path: Array.isArray(state.follow.path) ? state.follow.path : [],
        trail: Array.isArray(state.follow.path) ? state.follow.path : [],
      };
    }

    this._emit({
      type: "tick",
      tick: state.tick,
      phase: state.phase,
      units,
      engagements,
      projectiles,
      messengers,
      events,
      follow,
      structures: Array.isArray(state.structures) ? state.structures : [],
      gate_open: Array.isArray(state.gate_open) ? state.gate_open : [],
      structure_hp: Array.isArray(state.structure_hp) ? state.structure_hp : [],
      capture_points: Array.isArray(state.capture_points)
        ? state.capture_points
        : [],
      camps: Array.isArray(state.camps) ? state.camps : [],
      roads: Array.isArray(state.roads) ? state.roads : [],
      visibility_bitmap: state.visibility_bitmap ?? null,
      wall_build_projects: Array.isArray(state.wall_build_projects)
        ? state.wall_build_projects
        : [],
      wood_stockpile:
        state.wood_stockpile && typeof state.wood_stockpile === "object"
          ? state.wood_stockpile
          : {},
      harvest_tiles: Array.isArray(state.harvest_tiles)
        ? state.harvest_tiles
        : [],
      ant_groups: Array.isArray(state.ant_groups) ? state.ant_groups : [],
      terrain_overrides:
        state.terrain_overrides && typeof state.terrain_overrides === "object"
          ? state.terrain_overrides
          : {},
      watchtowers: Array.isArray(state.watchtowers) ? state.watchtowers : [],
    });
  }

  // --- Table accessors (convert SpacetimeDB rows to the format main.js expects) ---

  private _getMatchArmies(): any[] {
    if (!this.conn || !this._matchId) return [];
    const armies: any[] = [];
    for (const a of this.conn.db.match_armies.iter()) {
      if (a.matchId === this._matchId) {
        armies.push({
          index: a.armyIndex,
          team: a.team,
          name: a.name,
          commander_id: a.commanderId ?? null,
          player_id: a.playerId ?? null,
          player_name: a.playerName,
          color: a.color,
          persistent_army_id: a.persistentArmyId ?? null,
          roster_selected: a.rosterSelected,
          deployed: a.deployed,
          ready: a.ready,
          defeated: a.defeated,
          is_bot: a.isBot ?? false,
          follow_active: a.followActive,
          follow_path: (() => {
            try {
              return JSON.parse(a.followPathJson || "[]");
            } catch {
              return [];
            }
          })(),
        });
      }
    }
    return armies;
  }

  // Battle-time entity getters: when the worker bridge is active for this
  // match, the per-tick worker WS is the source of truth and we short-circuit
  // to its cached frame. Otherwise we fall back to the legacy SpacetimeDB
  // `my_*` row pipeline (still subscribed in `_subscribeToMatch`), which the
  // module continues to populate until a worker registers and the C2 gate in
  // `process_tick` flips the simulation off.

  private _getEngagementIdsByUnit(): Map<string, string[]> {
    const byUnit = new Map<string, string[]>();
    for (const engagement of this._getMatchEngagements()) {
      const a = byUnit.get(engagement.unit_a_id) || [];
      a.push(engagement.id);
      byUnit.set(engagement.unit_a_id, a);

      const b = byUnit.get(engagement.unit_b_id) || [];
      b.push(engagement.id);
      byUnit.set(engagement.unit_b_id, b);
    }
    return byUnit;
  }

  private _getUnitSkills(): Map<string, string[]> {
    const grouped = new Map<string, { ordinal: number; skill: string }[]>();
    if (!this.conn || !this._matchId) return new Map();
    for (const row of this.conn.db.my_unit_skills.iter()) {
      if (row.matchId !== this._matchId) continue;
      const list = grouped.get(row.unitId) || [];
      list.push({ ordinal: row.ordinal, skill: row.skillName });
      grouped.set(row.unitId, list);
    }

    const result = new Map<string, string[]>();
    for (const [unitId, skills] of grouped.entries()) {
      skills.sort((left, right) => left.ordinal - right.ordinal);
      result.set(
        unitId,
        skills.map((entry) => entry.skill),
      );
    }
    return result;
  }

  private _getUnitOrderQueue(): Map<string, any[]> {
    const grouped = new Map<string, any[]>();
    if (!this.conn || !this._matchId) return new Map();
    for (const row of this.conn.db.my_unit_order_queue.iter()) {
      if (row.matchId !== this._matchId) continue;
      const list = grouped.get(row.unitId) || [];
      list.push({
        ordinal: row.ordinal,
        order_type: row.orderType,
        target_x: row.targetX ?? null,
        target_y: row.targetY ?? null,
        facing: row.facing ?? null,
        target_unit_id: row.targetUnitId ?? null,
      });
      grouped.set(row.unitId, list);
    }
    for (const list of grouped.values()) {
      list.sort((left, right) => left.ordinal - right.ordinal);
    }
    return grouped;
  }

  private _getUnitPathWaypoints(): Map<string, any[]> {
    const grouped = new Map<
      string,
      { ordinal: number; point: [number, number] }[]
    >();
    if (!this.conn || !this._matchId) return new Map();
    for (const row of this.conn.db.my_unit_path_waypoints.iter()) {
      if (row.matchId !== this._matchId) continue;
      const list = grouped.get(row.unitId) || [];
      list.push({ ordinal: row.ordinal, point: [row.x, row.y] });
      grouped.set(row.unitId, list);
    }

    const result = new Map<string, any[]>();
    for (const [unitId, waypoints] of grouped.entries()) {
      waypoints.sort((left, right) => left.ordinal - right.ordinal);
      result.set(
        unitId,
        waypoints.map((entry) => entry.point),
      );
    }
    return result;
  }

  private _getFriendlyUnits(): any[] {
    if (this._workerBridge.isActive(this._matchId)) {
      return this._workerBridge.getFriendlyUnits();
    }
    if (this._isBattleBridgeOwned(this._matchId)) return [];
    if (!this.conn || !this._matchId) return [];
    const engagementIdsByUnit = this._getEngagementIdsByUnit();
    const skillsByUnit = this._getUnitSkills();
    const orderQueueByUnit = this._getUnitOrderQueue();
    const pathByUnit = this._getUnitPathWaypoints();

    const units: any[] = [];
    for (const row of this.conn.db.my_friendly_units.iter()) {
      if (row.matchId !== this._matchId) continue;
      const unit: any = {
        id: row.unitId,
        army_index: row.armyIndex,
        archetype: row.archetype,
        name: row.name,
        x: row.x,
        y: row.y,
        facing: row.facing,
        formation_shape: row.formationShape ?? null,
        artillery: parseBatteryArtillery(artilleryField(row)),
        length: row.length,
        width: row.width,
        state: row.state,
        stance: row.stance,
        marching: row.marching === true,
        column_parent_id: row.columnParentId ?? null,
        column_index: row.columnIndex ?? 0,
        column_phase: row.columnPhase ?? "none",
        strength: row.strength,
        max_strength: row.maxStrength,
        troops_alive: row.troopsAlive ?? 0,
        troops_max: row.troopsMax ?? 0,
        formation_files: row.formationFiles ?? 0,
        formation_ranks: row.formationRanks ?? 0,
        is_general: row.isGeneral,
        is_captain: (row as any).isCaptain ?? false,
        captain_unit_id: (row as any).captainUnitId ?? null,
        category: row.category,
        symbol: row.symbol,
        level: row.level,
        engagement_ids: engagementIdsByUnit.get(row.unitId) || [],
        is_ranged: row.isRanged,
        suppressed: row.suppressed,
        follow_mode: row.followMode,
        builder_enabled: (row as any).builderEnabled ?? false,
        garrisoned_in: row.garrisonedIn ?? null,
        morale: row.morale,
        max_morale: row.maxMorale,
        fatigue: row.fatigue,
        reinforced_sides: row.reinforcedSides,
        chain_front_unit_id: row.chainFrontUnitId ?? null,
        chain_rear_unit_id: row.chainRearUnitId ?? null,
        chain_depth: row.chainDepth,
        chain_size: row.chainSize,
        has_pending_order: row.hasPendingOrder,
        current_order_type: row.currentOrderType ?? null,
        order_queue: orderQueueByUnit.get(row.unitId) || [],
        skills: skillsByUnit.get(row.unitId) || [],
        experience: row.experience,
        idle_stance: row.idleStance,
        guard_point:
          row.guardPointX != null && row.guardPointY != null
            ? { x: row.guardPointX, y: row.guardPointY }
            : null,
        guard_radius: row.guardRadius,
        vision_range: row.visionRange,
        kills: row.kills ?? 0,
        xp_earned: row.xpEarned ?? 0,
      };

      if (row.persistentId) {
        unit.persistent_id = row.persistentId;
      }
      if (row.isRanged) {
        unit.ammo = row.ammo ?? 0;
        unit.max_ammo = row.maxAmmo ?? 0;
        unit.fire_range = row.fireRange ?? 0;
        unit.ranged_stance = row.rangedStance ?? "fire_at_will";
        unit.fire_at_will = row.fireAtWill ?? false;
      }
      if (row.moveTargetX != null && row.moveTargetY != null) {
        unit.move_target_x = row.moveTargetX;
        unit.move_target_y = row.moveTargetY;
      }
      if (row.moveTargetFacing != null) {
        unit.move_target_facing = row.moveTargetFacing;
      }
      const pathWaypoints = pathByUnit.get(row.unitId) || [];
      if (pathWaypoints.length > 0) {
        unit.path_waypoints = pathWaypoints;
      }

      units.push(unit);
    }
    return units;
  }

  private _getEnemyUnits(): any[] {
    if (this._workerBridge.isActive(this._matchId)) {
      return this._workerBridge.getEnemyUnits();
    }
    if (this._isBattleBridgeOwned(this._matchId)) return [];
    if (!this.conn || !this._matchId) return [];
    const engagementIdsByUnit = this._getEngagementIdsByUnit();
    const units: any[] = [];
    for (const row of this.conn.db.my_enemy_units.iter()) {
      if (row.matchId !== this._matchId) continue;
      const unit: any = {
        id: row.unitId,
        army_index: row.armyIndex,
        archetype: row.archetype,
        name: row.name,
        x: row.x,
        y: row.y,
        facing: row.facing,
        artillery: parseBatteryArtillery(artilleryField(row)),
        length: row.length,
        width: row.width,
        state: row.state,
        stance: row.stance,
        marching: row.marching === true,
        column_parent_id: row.columnParentId ?? null,
        column_index: row.columnIndex ?? 0,
        column_phase: row.columnPhase ?? "none",
        strength: row.strength,
        max_strength: row.maxStrength,
        troops_alive: row.troopsAlive ?? 0,
        troops_max: row.troopsMax ?? 0,
        formation_files: row.formationFiles ?? 0,
        formation_ranks: row.formationRanks ?? 0,
        is_general: row.isGeneral,
        category: row.category,
        symbol: row.symbol,
        level: row.level,
        engagement_ids: engagementIdsByUnit.get(row.unitId) || [],
        is_ranged: row.isRanged,
        suppressed: row.suppressed,
        follow_mode: row.followMode,
        garrisoned_in: row.garrisonedIn ?? null,
        kills: row.kills ?? 0,
        xp_earned: row.xpEarned ?? 0,
      };
      if (row.isRanged) {
        unit.ammo = row.ammo ?? 0;
        unit.max_ammo = row.maxAmmo ?? 0;
        unit.fire_range = row.fireRange ?? 0;
        unit.ranged_stance = row.rangedStance ?? "fire_at_will";
        unit.fire_at_will = row.fireAtWill ?? false;
      }
      units.push(unit);
    }
    return units;
  }

  private _getMatchUnits(): any[] {
    return [...this._getFriendlyUnits(), ...this._getEnemyUnits()];
  }

  private _getMatchEngagements(): any[] {
    if (this._workerBridge.isActive(this._matchId)) {
      return this._workerBridge.getEngagements();
    }
    if (this._isBattleBridgeOwned(this._matchId)) return [];
    if (!this.conn || !this._matchId) return [];
    const engagements: any[] = [];
    for (const row of this.conn.db.my_engagements.iter()) {
      if (row.matchId !== this._matchId) continue;
      engagements.push({
        id: row.engagementId,
        unit_a_id: row.unitAId,
        unit_b_id: row.unitBId,
        tick_started: row.tickStarted,
        ticks_active: row.ticksActive,
        direction_on_a: row.directionOnA,
        direction_on_b: row.directionOnB,
        damage_a_to_b: row.damageAToB,
        damage_b_to_a: row.damageBToA,
        morale_a_to_b: row.moraleAToB,
        morale_b_to_a: row.moraleBToA,
        outcome: row.outcome,
      });
    }
    return engagements;
  }

  private _getMatchProjectiles(): any[] {
    if (this._workerBridge.isActive(this._matchId)) {
      return this._workerBridge.getProjectiles();
    }
    if (this._isBattleBridgeOwned(this._matchId)) return [];
    if (!this.conn || !this._matchId) return [];
    const projectiles: any[] = [];
    for (const row of this.conn.db.my_projectiles.iter()) {
      if (row.matchId !== this._matchId) continue;
      projectiles.push({
        id: row.projectileId,
        source_unit_id: row.sourceUnitId,
        x: row.x,
        y: row.y,
        vx: row.vx,
        vy: row.vy,
        team_index: row.teamIndex,
        projectile_type: row.projectileType,
        progress: row.progress,
        volley: parseVolleyVisual(row.volleyJson),
        artillery: parseProjectileArtillery(artilleryField(row)),
      });
    }
    return projectiles;
  }

  private _getMatchMessengers(): any[] {
    if (this._workerBridge.isActive(this._matchId)) {
      return this._workerBridge.getMessengers();
    }
    if (this._isBattleBridgeOwned(this._matchId)) return [];
    if (!this.conn || !this._matchId) return [];
    const messengers: any[] = [];
    for (const row of this.conn.db.my_messengers.iter()) {
      if (row.matchId !== this._matchId) continue;
      messengers.push({
        id: row.messengerId,
        x: row.x,
        y: row.y,
        target_unit_id: row.targetUnitId,
        order_type: row.orderType,
      });
    }
    return messengers;
  }

  private _getMatchVisibilityBitmap(): any | null {
    if (this._workerBridge.isActive(this._matchId)) {
      return this._workerBridge.getVisibilityBitmap();
    }
    if (!this.conn || !this._matchId) return null;
    let row: any = null;
    for (const r of this.conn.db.my_visibility_bitmap.iter()) {
      if (r.matchId !== this._matchId) continue;
      row = r;
      break;
    }
    if (!row) return null;
    const cols = row.cols >>> 0;
    const rows = row.rows >>> 0;
    const totalBits = cols * rows;
    const packedLen = (totalBits + 7) >>> 3;
    const packed = new Uint8Array(packedLen);
    // RLE: 0x00 followed by count_u8 = run of `count` zero bytes; otherwise raw byte
    const rle: Uint8Array = row.rle instanceof Uint8Array
      ? row.rle
      : new Uint8Array(row.rle);
    let dst = 0;
    for (let i = 0; i < rle.length; i++) {
      const b = rle[i];
      if (b === 0x00) {
        const count = rle[++i] | 0;
        const end = Math.min(dst + count, packedLen);
        // bytes already initialized to 0
        dst = end;
      } else {
        if (dst < packedLen) packed[dst++] = b;
      }
    }
    return {
      cell_size: row.cellSize,
      cols,
      rows,
      bits: packed,
    };
  }

  private _getColumnFeedbackEvents(): any[] {
    const meta = this._getCurrentMatchMetaRow();
    if (!meta?.columnEventsJson || !Number.isSafeInteger(meta.armyIndex) || meta.armyIndex < 0) return [];
    const armyIndex = this._lookupMyArmyIndex();
    if (armyIndex != null && armyIndex !== meta.armyIndex) return [];
    let rows: any;
    try {
      rows = JSON.parse(meta.columnEventsJson);
    } catch {
      console.warn("[Network] Invalid caller-scoped column feedback snapshot.");
      return [];
    }
    if (!Array.isArray(rows) || rows.length > 8192) return [];
    const id = (value: any) => typeof value === "string" && value.length > 0 && value.length <= 1024;
    const reason = (value: any) => typeof value === "string" && value.length <= 4096;
    const seen = new Set<string>();
    const events: any[] = [];
    for (const row of rows) {
      if (!row || typeof row !== "object" || Array.isArray(row)
        || !COLUMN_FEEDBACK_EVENT_TYPES.has(row.type) || row.army_index !== meta.armyIndex
        || !Number.isSafeInteger(row.tick) || row.tick < 0) continue;
      const event: any = {type: row.type, army_index: row.army_index, tick: row.tick};
      if (row.type.startsWith("artillery_")) {
        const artillery = parseArtilleryEvent(row, meta.armyIndex);
        if (!artillery) continue;
        Object.assign(event, artillery);
      } else if (row.type === "follow_changed") {
        if (typeof row.active !== "boolean" || !Array.isArray(row.unit_ids) || row.unit_ids.length > 400
          || !row.unit_ids.every(id) || !Array.isArray(row.skipped) || row.skipped.length > 400
          || !row.skipped.every((entry: any) => entry && id(entry.unit_id) && reason(entry.reason))) continue;
        event.active = row.active;
        event.unit_ids = row.unit_ids;
        event.skipped = row.skipped.map((entry: any) => ({unit_id: entry.unit_id, reason: entry.reason}));
      } else {
        if (!reason(row.reason) || row.type === "column_deployment_blocked" && !id(row.unit_id)) continue;
        event.reason = row.reason;
        if (row.type === "column_deployment_blocked") event.unit_id = row.unit_id;
      }
      const key = JSON.stringify(event);
      if (seen.has(key)) continue;
      seen.add(key);
      events.push(event);
    }
    return events;
  }

  private _getMatchEvents(): any[] {
    if (!this.conn || !this._matchId) return [];
    const events: any[] = [];
    for (const row of this.conn.db.match_events.iter()) {
      if (row.matchId !== this._matchId) continue;
      if (/^(column_|follow_|army_follow_|artillery_)/.test(row.eventType ?? "")) continue;
      events.push({
        type: row.eventType ?? "unknown",
        unit_id: row.unitId ?? null,
        target_id: row.targetId ?? null,
        unit_a_id: row.unitAId ?? null,
        unit_b_id: row.unitBId ?? null,
        unit_name: row.unitName ?? null,
        unit_a_name: row.unitAName ?? null,
        unit_b_name: row.unitBName ?? null,
        army_index: row.armyIndex ?? null,
        structure_index: row.structureIndex ?? null,
        team: row.team ?? null,
        previous_team: row.previousTeam ?? null,
        order_type: row.orderType ?? null,
        friendly_fire: row.friendlyFire ?? null,
        bonus: row.bonus ?? null,
        tick: row.tick,
        row_id: Number(row.rowId),
      });
    }
    events.push(...this._getColumnFeedbackEvents());
    events.sort((left, right) => {
      if (left.tick !== right.tick) return left.tick - right.tick;
      return (left.row_id ?? 0) - (right.row_id ?? 0);
    });
    return events;
  }

  private _getSession(): any | null {
    if (!this.conn) return null;
    for (const session of this.conn.db.my_session.iter()) {
      return session;
    }
    return null;
  }

  private _getCurrentMatchMetaRow(): any | null {
    if (!this.conn || !this._matchId) return null;
    for (const row of this.conn.db.my_match_meta.iter()) {
      if (row.matchId === this._matchId) {
        return row;
      }
    }
    return null;
  }

  private _getMatchFollow(): any | null {
    if (this._workerBridge.isActive(this._matchId)) return this._workerBridge.getFollow();
    if (!this.conn || !this._matchId) return null;
    const meta = this._getCurrentMatchMetaRow();
    if (!meta) return null;

    const points: { ordinal: number; point: [number, number] }[] = [];
    for (const row of this.conn.db.my_match_follow_waypoints.iter()) {
      if (row.matchId !== this._matchId) continue;
      points.push({ ordinal: row.ordinal, point: [row.x, row.y] });
    }
    points.sort((left, right) => left.ordinal - right.ordinal);

    return {
      active: !!meta.followActive,
      path: points.map((entry) => entry.point),
    };
  }

  private _getMatchMapState(): {
    gate_open: boolean[];
    structure_hp: number[];
    structures: any[];
    capture_points: any[];
  } {
    if (this._workerBridge.isActive(this._matchId)) {
      return this._workerBridge.getMapState();
    }
    if (!this.conn || !this._matchId) {
      return { gate_open: [], structure_hp: [], structures: [], capture_points: [] };
    }

    const structureRows = [...this.conn.db.match_structure_state.iter()].filter(
      (row: any) => row.matchId === this._matchId,
    );
    const captureRows = [...this.conn.db.match_capture_points.iter()].filter(
      (row: any) => row.matchId === this._matchId,
    );

    const maxStructureIndex = structureRows.reduce(
      (max, row: any) => Math.max(max, row.structureIndex),
      -1,
    );
    const structureCount = Math.max(
      Array.isArray(this._mapData?.structures)
        ? this._mapData.structures.length
        : 0,
      maxStructureIndex + 1,
    );
    const gate_open = Array.from({ length: structureCount }, () => false);
    const structure_hp = Array.from({ length: structureCount }, () => 0);
    const structures: any[] = [];

    for (const row of structureRows) {
      const rowAny = row as any;
      gate_open[rowAny.structureIndex] = !!rowAny.gateOpen;
      structure_hp[rowAny.structureIndex] = rowAny.hp ?? 0;
      if (rowAny.structureType && rowAny.x != null && rowAny.y != null) {
        structures[rowAny.structureIndex] = {
          index: rowAny.structureIndex,
          type: rowAny.structureType,
          material: rowAny.material ?? null,
          x: rowAny.x,
          y: rowAny.y,
          width: rowAny.width ?? null,
          height: rowAny.height ?? null,
          radius: rowAny.radius ?? null,
          facing: rowAny.facing ?? null,
          owner_team: rowAny.ownerTeam ?? null,
          hp: rowAny.hp ?? 0,
          max_hp: rowAny.maxHp ?? 0,
          open: !!rowAny.gateOpen,
          built_in_match: !!rowAny.builtInMatch,
          built_by_army: rowAny.builtByArmy ?? null,
          built_tick: rowAny.builtTick ?? null,
          wall_group_id: rowAny.wallGroupId ?? null,
          last_known: !!rowAny.lastKnown,
        };
      }
    }

    const capture_points = captureRows
      .map((row: any) => ({
        structure_index: row.structureIndex,
        owner: row.owner ?? null,
        progress: row.progress,
        capturing_team: row.capturingTeam ?? null,
        hold_counter: row.holdCounter,
      }))
      .sort(
        (left: any, right: any) => left.structure_index - right.structure_index,
      );

    return { gate_open, structure_hp, structures, capture_points };
  }

  private _getMatchWatchtowers(): any[] {
    if (!this.conn || !this._matchId) return [];
    try {
      const tableAny = (this.conn.db as any).match_state;
      if (tableAny) {
        const row = tableAny.matchId?.find(this._matchId);
        if (row && row.watchtowersJson) {
          return JSON.parse(row.watchtowersJson);
        }
      }
    } catch (e) {
      console.warn("Failed to parse match_state watchtowersJson", e);
    }
    return [];
  }

  private _getMatchRoads(): any[] {
    if (!this.conn || !this._matchId) return [];
    for (const row of this.conn.db.my_match_roads.iter()) {
      if (row.matchId !== this._matchId) continue;
      try {
        const roads = JSON.parse(row.roadsJson);
        if (!Array.isArray(roads)) throw new Error("Road snapshot must be an array.");
        return roads;
      } catch (error) {
        console.error("[Network] Invalid authoritative road snapshot:", error);
        return [];
      }
    }
    return [];
  }

  private _getMatchWallBuildProjects(): any[] {
    if (!this.conn || !this._matchId) return [];
    // Public per-match snapshot (subscribed via _subscribeToMatch).
    let projectsJson: string | null = null;
    const tableAny = (this.conn.db as any).match_wall_build_projects;
    if (tableAny) {
      try {
        for (const row of tableAny.iter()) {
          if ((row as any).matchId === this._matchId) {
            projectsJson = (row as any).projectsJson ?? null;
            break;
          }
        }
      } catch {
        projectsJson = null;
      }
    }
    if (!projectsJson) return [];
    try {
      const parsed = JSON.parse(projectsJson);
      if (!Array.isArray(parsed)) return [];
      return parsed.map((project: any) => ({
        id: project.id,
        army_index: project.army_index,
        team_index: project.team_index,
        general_unit_id: project.general_unit_id,
        completed_tick: project.completed_tick ?? null,
        sections: Array.isArray(project.sections)
          ? project.sections.map((section: any) => ({
              center_x: section.center_x,
              center_y: section.center_y,
              facing: section.facing,
              length: section.length,
              width: section.width,
              progress: section.progress,
              completed: !!section.completed,
              structure_index: section.structure_index ?? null,
            }))
          : [],
      }));
    } catch {
      return [];
    }
  }

  private _getMatchCamps(): any[] {
    if (!this.conn || !this._matchId) return [];
    const tableAny = (this.conn.db as any).match_camps;
    if (!tableAny) return [];
    const camps: any[] = [];
    try {
      for (const row of tableAny.iter()) {
        if ((row as any).matchId !== this._matchId) continue;
        let tents: any[] = [];
        const tentsJson = (row as any).tentsJson ?? "[]";
        try {
          const parsed = JSON.parse(tentsJson);
          tents = Array.isArray(parsed) ? parsed : [];
        } catch {
          tents = [];
        }
        camps.push({
          id: (row as any).campId,
          army_index: (row as any).armyIndex,
          general_unit_id: (row as any).generalUnitId,
          x: (row as any).x,
          y: (row as any).y,
          radius: (row as any).radius,
          hp: (row as any).hp,
          max_hp: (row as any).maxHp,
          tent_count: (row as any).tentCount,
          max_tents: (row as any).maxTents,
          tents,
          placed_tick: (row as any).placedTick,
          completed: (row as any).completed ?? true,
          construction_progress: (row as any).constructionProgress ?? 0,
          construction_required: (row as any).constructionRequired ?? 0,
          last_known: !!(row as any).lastKnown,
          visibility_authoritative: true,
        });
      }
    } catch {
      return [];
    }
    return camps;
  }

  private _getCurrentMatchState(): any | null {
    if (!this.conn || !this._matchId) return null;
    const meta = this._getCurrentMatchMetaRow();
    const match = this.conn.db.matches.id.find(this._matchId);
    if (!meta && !match) return null;

    const workerActive = this._workerBridge.isActive(this._matchId);
    const mapState = this._getMatchMapState();
    return {
      tick: workerActive
        ? this._workerBridge.tick
        : (meta?.tick ?? ((match?.tick ?? 0) + (match?.startTickOffset ?? 0))),
      mode: match?.mode ?? null,
      phase: workerActive
        ? (this._workerBridge.phase || meta?.phase || match?.phase || "lobby")
        : (meta?.phase ?? match?.phase ?? "lobby"),
      units: this._getMatchUnits(),
      engagements: this._getMatchEngagements(),
      messengers: this._getMatchMessengers(),
      projectiles: this._getMatchProjectiles(),
      events: workerActive ? this._workerBridge.events : this._getMatchEvents(),
      follow: this._getMatchFollow(),
      gate_open: mapState.gate_open,
      structure_hp: mapState.structure_hp,
      structures: mapState.structures,
      capture_points: mapState.capture_points,
      watchtowers: workerActive
        ? this._workerBridge.getWatchtowers()
        : this._getMatchWatchtowers(),
      camps: workerActive ? this._workerBridge.getCamps() : this._getMatchCamps(),
      roads: workerActive ? this._workerBridge.getRoads() : this._getMatchRoads(),
      visibility_bitmap: this._getMatchVisibilityBitmap(),
      wall_build_projects: workerActive
        ? this._workerBridge.getWallBuildProjects()
        : this._getMatchWallBuildProjects(),
      wood_stockpile: workerActive
        ? this._workerBridge.getWoodStockpile()
        : this._getMatchWoodStockpile(),
      harvest_tiles: workerActive
        ? this._workerBridge.getHarvestTiles()
        : this._getMatchHarvestTiles(),
      ant_groups: workerActive
        ? this._workerBridge.getAntGroups()
        : this._getMatchAntGroups(),
      terrain_overrides: workerActive
        ? this._workerBridge.getTerrainOverrides()
        : this._getMatchTerrainOverrides(),
    };
  }

  private _getMatchEconomyRowJsons(): { wood: string | null; tiles: string | null } {
    if (!this.conn || !this._matchId) return { wood: null, tiles: null };
    const tableAny = (this.conn.db as any).match_economy;
    if (!tableAny) return { wood: null, tiles: null };
    try {
      for (const row of tableAny.iter()) {
        if ((row as any).matchId === this._matchId) {
          return {
            wood: (row as any).woodJson ?? null,
            tiles: (row as any).harvestTilesJson ?? null,
          };
        }
      }
    } catch {
      return { wood: null, tiles: null };
    }
    return { wood: null, tiles: null };
  }

  private _getMatchWoodStockpile(): Record<number, number> {
    const { wood } = this._getMatchEconomyRowJsons();
    if (!wood) return {};
    try {
      const parsed = JSON.parse(wood);
      if (!parsed || typeof parsed !== "object") return {};
      const out: Record<number, number> = {};
      for (const [k, v] of Object.entries(parsed)) {
        const idx = Number(k);
        if (!Number.isFinite(idx)) continue;
        out[idx] = Number(v) | 0;
      }
      return out;
    } catch {
      return {};
    }
  }

  private _getMatchHarvestTiles(): any[] {
    const { tiles } = this._getMatchEconomyRowJsons();
    if (!tiles) return [];
    try {
      const parsed = JSON.parse(tiles);
      if (!Array.isArray(parsed)) return [];
      return parsed.map((tile: any) => ({
        army_index: tile.army_index | 0,
        tile_x: tile.tile_x | 0,
        tile_y: tile.tile_y | 0,
        wood_remaining: tile.wood_remaining | 0,
      }));
    } catch {
      return [];
    }
  }

  private _getMatchAntGroups(): any[] {
    if (!this.conn || !this._matchId) return [];
    const tableAny = (this.conn.db as any).match_ant_groups;
    if (!tableAny) return [];
    let json: string | null = null;
    try {
      for (const row of tableAny.iter()) {
        if ((row as any).matchId === this._matchId) {
          json = (row as any).groupsJson ?? null;
          break;
        }
      }
    } catch {
      return [];
    }
    if (!json) return [];
    try {
      const parsed = JSON.parse(json);
      if (!Array.isArray(parsed)) return [];
      return parsed;
    } catch {
      return [];
    }
  }

  private _getMatchTerrainOverrides(): Record<string, number> {
    if (!this.conn || !this._matchId) return {};
    const tableAny = (this.conn.db as any).match_terrain_overrides;
    if (!tableAny) return {};
    let json: string | null = null;
    try {
      for (const row of tableAny.iter()) {
        if ((row as any).matchId === this._matchId) {
          json = (row as any).overridesJson ?? null;
          break;
        }
      }
    } catch {
      return {};
    }
    if (!json) return {};
    try {
      const parsed = JSON.parse(json);
      if (!parsed || typeof parsed !== "object") return {};
      return parsed as Record<string, number>;
    } catch {
      return {};
    }
  }

  private _mapCampaignArmy(row: any) {
    return {
      id: row.id,
      player_id: row.playerId,
      world_id: row.worldId,
      persistent_army_id: row.persistentArmyId,
      name: row.name,
      x: row.x,
      y: row.y,
      destination_x: row.destinationX ?? null,
      destination_y: row.destinationY ?? null,
      status: row.status,
      current_city_id: row.currentCityId ?? null,
      active_battle_id: row.activeBattleId ?? null,
      speed_per_second: row.speedPerSecond,
      updated_at: row.updatedAt,
      is_mine: row.playerId === this._playerId,
    };
  }

  private _mapCampaignCity(row: any) {
    return {
      id: row.id,
      world_id: row.worldId,
      structure_index: row.structureIndex,
      name: row.name,
      tier: row.tier,
      x: row.x,
      y: row.y,
      radius: row.radius,
      owner_player_id: row.ownerPlayerId ?? null,
      gold_per_tick: row.goldPerTick,
      manpower_per_tick: row.manpowerPerTick,
      supplies_per_tick: row.suppliesPerTick,
      updated_at: row.updatedAt,
      owned_by_me: row.ownerPlayerId === this._playerId,
    };
  }

  private _mapCampaignRecruitment(row: any) {
    return {
      row_id: row.rowId,
      player_id: row.playerId,
      world_id: row.worldId,
      city_id: row.cityId,
      army_id: row.armyId,
      archetype: row.archetype,
      count: row.count,
      ticks_remaining: row.ticksRemaining,
      status: row.status,
      created_at: row.createdAt,
    };
  }

  private _mapCampaignEvent(row: any) {
    return {
      row_id: row.rowId,
      world_id: row.worldId,
      tick: row.tick,
      event_type: row.eventType,
      player_id: row.playerId ?? null,
      army_id: row.armyId ?? null,
      city_id: row.cityId ?? null,
      battle_id: row.battleId ?? null,
      message: row.message,
      created_at: row.createdAt,
    };
  }

  private _getCampaignState(): any | null {
    if (!this.conn) return null;
    const world = [...this.conn.db.campaign_worlds.iter()][0] || null;
    const player = [...this.conn.db.my_campaign_player.iter()][0] || null;
    const resources = [...this.conn.db.my_campaign_resources.iter()][0] || null;
    const myArmy = [...this.conn.db.my_campaign_army.iter()][0] || null;
    const cities = [...this.conn.db.campaign_cities.iter()]
      .map((row: any) => this._mapCampaignCity(row))
      .sort((left: any, right: any) => left.id.localeCompare(right.id));
    const armies = [...this.conn.db.campaign_armies.iter()]
      .map((row: any) => this._mapCampaignArmy(row))
      .sort((left: any, right: any) => left.id.localeCompare(right.id));
    const recruitment = [...this.conn.db.my_campaign_recruitment.iter()]
      .map((row: any) => this._mapCampaignRecruitment(row))
      .sort((left: any, right: any) =>
        this._compareSortable(left.row_id, right.row_id),
      );
    const battles = [...this.conn.db.campaign_battles.iter()].map((row: any) => ({
      id: row.id,
      world_id: row.worldId,
      match_id: row.matchId ?? null,
      attacker_army_id: row.attackerArmyId,
      defender_army_id: row.defenderArmyId,
      x: row.x,
      y: row.y,
      status: row.status,
      created_at: row.createdAt,
      resolved_at: row.resolvedAt ?? null,
    }));

    if (!world && !player && !myArmy) return null;

    return {
      world: world
        ? {
            id: world.id,
            map_name: world.mapName,
            phase: world.phase,
            tick: world.tick,
            width: world.width,
            height: world.height,
            created_at: world.createdAt,
            updated_at: world.updatedAt,
          }
        : null,
      player: player
        ? {
            player_id: player.playerId,
            world_id: player.worldId,
            player_name: player.playerName,
            joined_at: player.joinedAt,
            last_seen_at: player.lastSeenAt,
          }
        : null,
      resources: resources
        ? {
            gold: resources.gold,
            manpower: resources.manpower,
            supplies: resources.supplies,
            updated_at: resources.updatedAt,
          }
        : null,
      my_army: myArmy ? this._mapCampaignArmy(myArmy) : null,
      armies,
      cities,
      battles,
      recruitment,
    };
  }

  private _emitCampaignState() {
    const state = this._getCampaignState();
    if (!state) return;
    this._emit({ type: "campaign_state", state });
  }

  private _mapConquestProfile(row: any) {
    return {
      id: row.id,
      player_id: row.playerId,
      ruleset: row.ruleset,
      player_name: row.playerName,
      avatar_name: row.avatarName,
      avatar_status: row.avatarStatus,
      selected_starter_territory_id: row.selectedStarterTerritoryId ?? null,
      controlled_territory_count: row.controlledTerritoryCount,
      replenishment_rate: row.replenishmentRate,
      unlocked_units_json: row.unlockedUnitsJson,
      unlocked_tech_json: row.unlockedTechJson,
      culture: row.culture ?? "",
      army_banner_id: row.armyBannerId ?? null,
      army_color: row.armyColor ?? null,
      army_marker_territory_id: row.armyMarkerTerritoryId ?? null,
      army_marker_x: row.armyMarkerX ?? null,
      army_marker_y: row.armyMarkerY ?? null,
      created_at: row.createdAt,
      updated_at: row.updatedAt,
    };
  }

  private _mapConquestCustomUnitTemplate(row: any) {
    return {
      id: row.id,
      player_id: row.playerId,
      ruleset: row.ruleset,
      culture: row.culture ?? "",
      display_name: row.displayName,
      melee_weapon: row.meleeWeapon,
      shield: row.shield,
      armor: row.armor,
      ranged_weapon: row.rangedWeapon,
      mount: row.mount,
      training: row.training,
      weapon_training_rank: row.weaponTrainingRank ?? 0,
      shield_training_rank: row.shieldTrainingRank ?? 0,
      formation_training_rank: row.formationTrainingRank ?? 0,
      endurance_training_rank: row.enduranceTrainingRank ?? 0,
      skirmishing_training_rank: row.skirmishingTrainingRank ?? 0,
      horsemanship_training_rank: row.horsemanshipTrainingRank ?? 0,
      fieldcraft_training_rank: row.fieldcraftTrainingRank ?? 0,
      created_at: row.createdAt,
      updated_at: row.updatedAt,
    };
  }

  private _mapConquestTerritory(row: any) {
    return {
      id: row.id,
      player_id: row.playerId,
      ruleset: row.ruleset,
      territory_id: row.territoryId,
      name: row.name,
      status: row.status,
      controlled: row.controlled,
      is_starter: row.isStarter,
      tier: row.tier,
      x: row.x,
      y: row.y,
      battle_maps_json: row.battleMapsJson,
      unlock_units_json: row.unlockUnitsJson,
      unlock_tech_json: row.unlockTechJson,
      updated_at: row.updatedAt,
    };
  }

  private _mapConquestQueue(row: any) {
    return {
      row_id: row.rowId,
      player_id: row.playerId,
      ruleset: row.ruleset,
      territory_id: row.territoryId,
      status: row.status,
      matched_match_id: row.matchedMatchId ?? null,
      created_at: row.createdAt,
      updated_at: row.updatedAt,
    };
  }

  private _microsToSeconds(value: any): number {
    const micros = typeof value === "bigint" ? Number(value) : Number(value ?? 0);
    if (!Number.isFinite(micros)) return 0;
    return Math.max(0, Math.ceil(micros / 1_000_000));
  }

  private _mapConquestLobbyTimer(row: any) {
    if (!row) return null;
    return {
      match_id: row.matchId,
      phase: row.phase || "building",
      build_remaining_micros: row.buildRemainingMicros,
      build_remaining_seconds: this._microsToSeconds(row.buildRemainingMicros),
      start_remaining_micros: row.startRemainingMicros,
      start_remaining_seconds: this._microsToSeconds(row.startRemainingMicros),
      start_locked: !!row.startLocked,
      created_at: row.createdAt,
      updated_at: row.updatedAt,
    };
  }

  private _getConquestLobbyTimer(matchId: string | null | undefined) {
    if (!this.conn || !matchId) return null;
    const table = (this.conn.db as any).conquest_lobby_timers;
    if (!table) return null;
    for (const row of table.iter()) {
      if (row.matchId === matchId) return this._mapConquestLobbyTimer(row);
    }
    return null;
  }

  private _mapConquestChallenge(row: any) {
    return {
      id: row.id,
      ruleset: row.ruleset,
      territory_id: row.territoryId,
      challenger_player_id: row.challengerPlayerId,
      defender_player_id: row.defenderPlayerId,
      status: row.status,
      source_match_id: row.sourceMatchId ?? null,
      challenge_match_id: row.challengeMatchId ?? null,
      created_at: row.createdAt,
      resolved_at: row.resolvedAt ?? null,
    };
  }

  private _mapConquestEvent(row: any) {
    return {
      row_id: row.rowId,
      player_id: row.playerId,
      ruleset: row.ruleset,
      event_type: row.eventType,
      territory_id: row.territoryId ?? null,
      challenge_id: row.challengeId ?? null,
      match_id: row.matchId ?? null,
      message: row.message,
      created_at: row.createdAt,
    };
  }

  private _mapConquestSavedUnit(row: any) {
    return {
      id: row.id,
      player_id: row.playerId,
      ruleset: row.ruleset,
      archetype: row.archetype,
      custom_name: row.customName ?? null,
      level: row.level,
      experience: row.experience,
      troops_percent: row.troopsPercent,
      kills: row.kills,
      battles_survived: row.battlesSurvived,
      skills_json: row.skillsJson,
    };
  }

  private _getConquestState(): any | null {
    if (!this.conn) return null;
    const conquestProfilesTable = (this.conn.db as any).my_conquest_profiles_v2 ?? this.conn.db.my_conquest_profiles;
    const profiles = [...conquestProfilesTable.iter()]
      .map((row: any) => this._mapConquestProfile(row))
      .sort((left: any, right: any) => left.ruleset.localeCompare(right.ruleset));
    const customUnitsTable = (this.conn.db as any).my_conquest_custom_unit_templates;
    const custom_units = customUnitsTable
      ? [...customUnitsTable.iter()]
          .map((row: any) => this._mapConquestCustomUnitTemplate(row))
          .sort((left: any, right: any) => {
            const rulesetOrder = left.ruleset.localeCompare(right.ruleset);
            if (rulesetOrder !== 0) return rulesetOrder;
            return left.culture.localeCompare(right.culture);
          })
      : [];
    const territories = [...this.conn.db.my_conquest_territories.iter()]
      .map((row: any) => this._mapConquestTerritory(row))
      .sort((left: any, right: any) => {
        const rulesetOrder = left.ruleset.localeCompare(right.ruleset);
        if (rulesetOrder !== 0) return rulesetOrder;
        return left.tier - right.tier || left.name.localeCompare(right.name);
      });
    const queue = [...this.conn.db.my_conquest_queue.iter()]
      .map((row: any) => this._mapConquestQueue(row))
      .sort((left: any, right: any) =>
        this._compareSortable(left.row_id, right.row_id),
      );
    const challenges = [...this.conn.db.my_conquest_challenges.iter()]
      .map((row: any) => this._mapConquestChallenge(row))
      .sort((left: any, right: any) =>
        this._compareSortable(left.created_at, right.created_at),
      );
    const events = [...this.conn.db.my_conquest_events.iter()]
      .map((row: any) => this._mapConquestEvent(row))
      .sort((left: any, right: any) =>
        this._compareSortable(right.row_id, left.row_id),
      );

    const savedUnitsTable = (this.conn.db as any).my_conquest_saved_units;
    const saved_units = savedUnitsTable
      ? [...savedUnitsTable.iter()]
          .map((row: any) => this._mapConquestSavedUnit(row))
          .sort((left: any, right: any) => {
            const rulesetOrder = left.ruleset.localeCompare(right.ruleset);
            if (rulesetOrder !== 0) return rulesetOrder;
            return left.archetype.localeCompare(right.archetype);
          })
      : [];

    if (
      profiles.length === 0 &&
      custom_units.length === 0 &&
      territories.length === 0 &&
      queue.length === 0 &&
      challenges.length === 0 &&
      events.length === 0 &&
      saved_units.length === 0
    ) {
      return null;
    }

    return { profiles, custom_units, territories, queue, challenges, events, saved_units };
  }

  private _emitConquestState() {
    if (this._conquestEmitTimer) {
      clearTimeout(this._conquestEmitTimer);
    }
    this._conquestEmitTimer = setTimeout(() => {
      this._conquestEmitTimer = null;
      this._emitConquestStateNow();
    }, 50);
  }

  private _arenaEmitTimer: ReturnType<typeof setTimeout> | null = null;

  private _emitArenaState() {
    if (this._arenaEmitTimer) {
      clearTimeout(this._arenaEmitTimer);
    }
    this._arenaEmitTimer = setTimeout(() => {
      this._arenaEmitTimer = null;
      this._emitArenaStateNow();
    }, 50);
  }

  private _emitArenaStateNow() {
    if (!this.conn) return;
    const inQueue = this.conn.db.arena_matchmaking_queue.player_id.find(this._playerId);
    const inLobbyPlayer = this.conn.db.arena_matchmaking_lobby_players.player_id.find(this._playerId);
    
    let status = "none";
    let lobbyInfo: any = null;
    let matchedMatchId: string | null = null;
    
    if (inQueue) {
      status = "queued";
    } else if (inLobbyPlayer) {
      const lobby = this.conn.db.arena_matchmaking_lobbies.lobby_id.find(inLobbyPlayer.lobbyId);
      if (lobby) {
        if (lobby.matchId) {
          status = "matched";
          matchedMatchId = lobby.matchId;
        } else {
          status = "lobby";
        }
        
        const players = [];
        for (const lp of this.conn.db.arena_matchmaking_lobby_players.iter()) {
          if (lp.lobbyId === lobby.lobbyId) {
            let unitArchetypes: string[] = [];
            try {
              unitArchetypes = JSON.parse(lp.unitArchetypes);
            } catch {}
            players.push({
              player_id: lp.playerId,
              player_name: lp.playerName,
              team: lp.team,
              general_archetype: lp.generalArchetype,
              unit_archetypes: unitArchetypes,
              joined_at: lp.joinedAt,
            });
          }
        }
        
        lobbyInfo = {
          lobby_id: lobby.lobbyId,
          timer_scheduled_at: lobby.timerScheduledAt,
          created_at: lobby.createdAt,
          players,
        };
      }
    }
    
    this._emit({
      type: "arena_state",
      state: {
        status,
        player_id: this._playerId,
        lobby: lobbyInfo,
        match_id: matchedMatchId,
      }
    });

    if (status === "matched" && matchedMatchId && matchedMatchId !== this._matchId) {
      this._emit({ type: "arena_matched", match_id: matchedMatchId });
    }
  }

  private _emitConquestStateNow() {
    const state = this._getConquestState();
    if (!state) return;
    this._emit({ type: "conquest_state", state });
    this._maybeOpenMatchedConquestMatch(state);
  }

  private _maybeOpenMatchedConquestMatch(state: any) {
    const matched = (state.queue || []).find(
      (row: any) => row.status === "matched" && row.matched_match_id,
    );
    const matchId = matched?.matched_match_id;
    const existingMatch = matchId && this.conn?.db.matches.id.find(matchId);
    if (
      !matchId ||
      this._finishedConquestMatchIds.has(matchId) ||
      existingMatch?.phase === "finished" ||
      matchId === this._matchId ||
      matchId === this._openingConquestMatchId
    ) {
      return;
    }

    if (this._notifiedConquestMatchIds.has(matchId)) return;
    this._notifiedConquestMatchIds.add(matchId);
    this._emit({ type: "conquest_matched", match_id: matchId });
  }

  private _compareSortable(left: any, right: any): number {
    const leftValue = this._toSortableValue(left);
    const rightValue = this._toSortableValue(right);
    if (leftValue === rightValue) return 0;
    if (leftValue == null) return -1;
    if (rightValue == null) return 1;

    if (
      (typeof leftValue === "bigint" || typeof leftValue === "number") &&
      (typeof rightValue === "bigint" || typeof rightValue === "number")
    ) {
      return leftValue < rightValue ? -1 : 1;
    }

    if (leftValue < rightValue) return -1;
    if (leftValue > rightValue) return 1;
    return String(leftValue).localeCompare(String(rightValue));
  }

  private _toSortableValue(value: any): any {
    if (value == null) return value;
    if (typeof value !== "object") return value;
    if ("microsSinceUnixEpoch" in value) return value.microsSinceUnixEpoch;
    if ("nanosSinceUnixEpoch" in value) return value.nanosSinceUnixEpoch;
    if (typeof value.valueOf === "function") {
      const primitive = value.valueOf();
      if (primitive !== value) return primitive;
    }
    return String(value);
  }

  private async _waitForSession(
    predicate: (session: any | null) => boolean,
    timeoutMs = 15000,
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const check = () => {
        if (this._subscriptionError) {
          reject(new Error(this._subscriptionError));
          return;
        }
        const session = this._getSession();
        if (predicate(session)) {
          resolve(session);
          return;
        }
        if (Date.now() - start >= timeoutMs) {
          reject(new Error("Session update timeout"));
          return;
        }
        setTimeout(check, 100);
      };
      check();
    });
  }

  // --- Emit message to main.js ---

  private _emit(data: any) {
    if (this.onMessage) this.onMessage(data);
  }

  private async _waitForConnectionReady(timeoutMs = 15000): Promise<void> {
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const check = () => {
        // Auth reducers only need an established connection identity.
        // `isActive` can lag behind on some builds and cause false login/register timeouts.
        if (this._identity || this._subscriptionReady || this.conn?.isActive) {
          resolve();
          return;
        }
        if (this._subscriptionError) {
          reject(new Error(this._subscriptionError));
          return;
        }
        if (Date.now() - start >= timeoutMs) {
          reject(
            new Error(
              `Not connected to ${this._stdbDatabase} at ${this._stdbUrl}`,
            ),
          );
          return;
        }
        setTimeout(check, 100);
      };
      check();
    });
  }

  private async _emitMatchJoined(matchId: string) {
    if (!this.conn) return;
    const match = this.conn.db.matches.id.find(matchId);
    const isHost = match?.hostPlayerId === this._playerId;
    const mapName = match?.mapName || "default";
    const mapData = await this._loadMapData(mapName);
    this._mapData = mapData;
    const armies = this._getMatchArmies();
    this._emit({
      type: "match_joined",
      match_id: matchId,
      map: mapData,
      phase: match?.phase || "lobby",
      is_host: isHost,
      armies,
      army_budget: match?.armyBudget || 150,
      max_army_size: match?.maxArmySize || 15,
      mode: ((match as any)?.mode as string | null | undefined) || "skirmish",
      ruleset:
        ((match as any)?.ruleset as string | null | undefined) || "classical",
      season: ((match as any)?.season as string | null | undefined) || "summer",
      conquest_lobby_timer: this._getConquestLobbyTimer(matchId),
    });
  }

  private async _restoreActiveMatchContext(matchId: string) {
    if (!this.conn || !matchId) return;
    this._matchId = matchId;
    this._lastEmittedTick = null;
    this._stopLobbySubscription();
    await this._subscribeToMatch(matchId);
    const match = this.conn.db.matches.id.find(matchId);
    if (!match) {
      this._finishedConquestMatchIds.add(matchId);
      this._matchId = null;
      this._lastEmittedTick = null;
      this._clearMatchSubscription();
      this._emitConquestState();
      return;
    }
    if (match?.phase === "finished") {
      if (((match as any)?.mode as string | null | undefined) === "conquest") {
        this._finishedConquestMatchIds.add(matchId);
      }
      this._emitMatchEnded(match);
      return;
    }
    await this._emitMatchJoined(matchId);
    this._emitTickUpdate();
  }

  private _emitActiveMatchAvailable(): void {
    if (!this.conn || this._matchId) return;
    const session = this._getSession();
    if (!session?.authenticated) return;

    const rows = [...this.conn.db.my_match_meta.iter()]
      .map((row: any) => {
        const matchId = String(row.matchId ?? "");
        const match = matchId ? this.conn!.db.matches.id.find(matchId) : null;
        return { row, match };
      })
      .filter(({ row, match }: any) => {
        const phase = String(row.phase ?? "");
        if (phase !== "running" && phase !== "setup") return false;
        const matchPhase = String(match?.phase ?? "");
        return matchPhase === "running" || matchPhase === "setup";
      })
      .sort((left: any, right: any) => Number(right.row.tick ?? 0) - Number(left.row.tick ?? 0));
    const candidate = rows[0];
    const row = candidate?.row;
    const match = candidate?.match;
    const matchId = String(row?.matchId ?? "");
    if (!matchId || this._notifiedActiveMatchIds.has(matchId)) return;

    this._notifiedActiveMatchIds.add(matchId);
    this._emit({
      type: "active_match_available",
      match_id: matchId,
      phase: match?.phase ?? row.phase ?? "running",
      tick: row.tick ?? 0,
      army_index: row.armyIndex ?? null,
    });
  }

  private _logicalResultUnits(units: any[]): any[] {
    const groups = new Map<string, any[]>();
    for (const unit of units) {
      const key = `${unit.army_index}:${unit.column_parent_id || unit.id}`;
      const group = groups.get(key) || [];
      group.push(unit);
      groups.set(key, group);
    }
    return [...groups.values()].map(blocks => {
      const id = blocks[0].column_parent_id || blocks[0].id;
      const original = blocks.find(unit => unit.id === id) || blocks[0];
      const live = blocks.find(unit => !["dead", "destroyed"].includes(unit.state));
      const unit = {...original, id, state: live?.state || original.state};
      for (const field of ["strength", "max_strength", "troops_alive", "troops_max",
        "ammo", "max_ammo", "kills", "xp_earned", "experience"]) {
        if (blocks.some(block => Number.isFinite(block[field]))) {
          unit[field] = blocks.reduce((total, block) => total + (Number(block[field]) || 0), 0);
        }
      }
      unit.level = Math.max(...blocks.map(block => block.level || 1));
      return unit;
    });
  }

  private _emitMatchEnded(matchRow: any) {
    const mode = ((matchRow as any)?.mode as string | null | undefined) || "skirmish";
    if (mode === "conquest") {
      this._finishedConquestMatchIds.add(matchRow.id);
    }
    const armies = this._getMatchArmies();
    const state = this._getCurrentMatchState();
    const units = this._logicalResultUnits(Array.isArray(state?.units) ? state.units : []);

    const resultArmies = armies.map((a: any) => ({
      army_index: a.index,
      player_name: a.player_name || `Army ${a.index + 1}`,
      team: a.team,
      units: units
        .filter((u: any) => u.army_index === a.index)
        .map((u: any) => ({
          id: u.id,
          archetype: u.archetype,
          persistent_id: u.persistent_id || null,
          level: u.level || 1,
          experience: u.experience || 0,
          name: u.name || u.archetype,
          survived: !["dead", "destroyed"].includes(u.state),
          strength: u.strength,
          max_strength: u.max_strength,
          troops_alive: u.troops_alive,
          troops_max: u.troops_max,
          ammo: u.ammo,
          max_ammo: u.max_ammo,
          kills: u.kills || 0,
          xp_earned: u.xp_earned || 0,
        })),
    }));

    this._emit({
      type: "match_ended",
      match_id: matchRow.id,
      mode,
      ruleset:
        ((matchRow as any)?.ruleset as string | null | undefined) || "classical",
      results: {
        winner: matchRow.winner ?? null,
        armies: resultArmies,
      },
    });
    this._matchId = null;
    this._lastEmittedTick = null;
    this._clearMatchSubscription();
    if (mode === "conquest") {
      this._emitConquestState();
    }
  }

  /** Standalone-only: synthesize a `match_ended` payload from the embedded
   *  worker's final summary. The SpacetimeDB match row still exists (the
   *  lobby created it) but its `phase` and `winner` columns are never
   *  updated by the standalone host, so we read the row for mode / ruleset
   *  and overlay the winner / phase from the worker. */
  private _emitStandaloneMatchEnded(end: WorkerMatchEnd): void {
    const matchId = end.match_id;
    this._locallyFinishedMatchIds.add(matchId);
    const dbRow = this.conn?.db.matches.id.find(matchId) || null;
    const isHost = (dbRow as any)?.hostPlayerId === this._playerId;
    if (isHost && this.conn) {
      try {
        const eventsJson =
          end.events == null ? "[]" : JSON.stringify(end.events);
        const summaryJson =
          end.summary == null ? "{}" : JSON.stringify(end.summary);
        void this.conn.reducers
          .submitMatchResult({
            matchId,
            winner: end.winner ?? undefined,
            summaryJson,
            eventsJson,
          })
          .then(() => this._emitConquestState())
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to submit match result");
          });
      } catch (err) {
        console.warn(
          "[standalone-battle] submitMatchResult failed",
          err,
        );
      }
    }
    const synthetic = {
      id: matchId,
      mode: (dbRow as any)?.mode || "skirmish",
      ruleset: (dbRow as any)?.ruleset || "classical",
      phase: "finished",
      winner: end.winner ?? null,
    };
    this._matchId = matchId;
    this._emitMatchEnded(synthetic as any);
  }

  private _emitReducerError(error: any, fallbackMessage: string) {
    const message = error?.message || String(error || fallbackMessage);
    this._emit({ type: "error", message });
  }

  private _sqlStringLiteral(value: string): string {
    return `'${`${value ?? ""}`.replace(/'/g, "''")}'`;
  }

  private _currentLobbyMatches(): any[] {
    if (!this.conn) return [];
    return [...this.conn.db.skirmish_lobby_matches.iter()];
  }

  private _ensureLobbySubscription(): Promise<void> {
    if (!this.conn) return Promise.reject(new Error("Not connected"));
    if (this._lobbySubscription?.isActive) return Promise.resolve();
    if (this._lobbySubscription) {
      try {
        this._lobbySubscription.unsubscribe();
      } catch {
        /* noop */
      }
      this._lobbySubscription = null;
    }

    return new Promise((resolve, reject) => {
      this._lobbySubscription = this.conn!.subscriptionBuilder()
        .onApplied(() => {
          this._emitMatchList();
          this._maybeEmitPendingCreatedMatch();
          resolve();
        })
        .onError((ctx: any) => {
          const error = ctx?.event?.error ?? ctx?.error ?? ctx;
          reject(error);
        })
        .subscribe(["SELECT * FROM skirmish_lobby_matches"]);
    });
  }

  private _stopLobbySubscription() {
    if (!this._lobbySubscription) return;
    try {
      this._lobbySubscription.unsubscribe();
    } catch {
      // Ignore unsubscribe races during reconnect / navigation churn.
    }
    this._lobbySubscription = null;
  }

  private _clearMatchSubscription() {
    if (!this._matchSubscription) return;
    try {
      this._matchSubscription.unsubscribe();
    } catch {
      // Ignore unsubscribe races during reconnect / navigation churn.
    }
    this._matchSubscription = null;
    this._matchSubscriptionId = null;
    this._lastEmittedTick = null;
    this._matchHostByMatch.clear();
    this._standaloneStartPacketByMatch.clear();
    this._closeWorkerWs("match_unsubscribed");
    this.closeStandaloneBattle("match_unsubscribed");
  }

  // ─── Phase 5 (parallel) — battle-worker WebSocket plumbing ────────────────

  /** Called when a `my_match_worker_endpoint` row is inserted or updated.
   *  Decides whether to (re)open the worker WebSocket. */
  private _onWorkerEndpointRow(row: any): void {
    if (!row) return;
    const matchId = String(row.matchId ?? row.match_id ?? "");
    const endpointUrl = String(row.endpointUrl ?? row.endpoint_url ?? "");
    const token = String(row.clientToken ?? row.client_token ?? "");
    if (!matchId || !endpointUrl || !token) return;
    if (!isWorkerWsEnabled()) {
      // Flag off — record the row but do not open. Renderer keeps using
      // the existing SpacetimeDB row subscriptions.
      console.info(
        "[worker-ws] endpoint published but USE_WORKER_WS=0; ignoring",
        { matchId, endpointUrl },
      );
      return;
    }
    const match = this.conn ? this.conn.db.matches.id.find(matchId) : null;
    const isArena = match ? (match.mode === "arena") : false;
    if (isStandaloneBattleEnabled() && !isArena) {
      // Standalone is the only supported battle transport in this build;
      // the orchestrator-hosted sim must never run in parallel with the
      // embedded host, otherwise two sims race over the same renderer.
      console.info(
        "[worker-ws] suppressed: standalone battle is the only transport",
        { matchId },
      );
      return;
    }
    // If we already have an open WS for the same endpoint, do nothing.
    if (
      this._workerWs &&
      this._workerWs.isOpen &&
      this._workerWsMatchId === matchId &&
      this._workerWsEndpointUrl === endpointUrl &&
      this._workerWsToken === token
    ) {
      return;
    }
    this._closeWorkerWs("endpoint_changed");
    this._openWorkerWs(matchId, endpointUrl, token);
  }

  // ─── Phase 5c — standalone host-election plumbing ─────────────────────────

  /** Public: have the local player attempt to become the standalone host
   *  for `matchId`. Idempotent and safe to call from anywhere; the module
   *  reducer rejects non-participants and noops when the row already
   *  exists. Errors are logged, not thrown — callers race with peers and
   *  losing the race is normal. */
  claimMatchHost(matchId: string): void {
    if (!this.conn || !matchId) return;
    try {
      const result = this.conn.reducers.claimMatchHost({ matchId }) as any;
      if (result && typeof result.catch === "function") {
        void result.catch((err: any) => {
          const msg = String(err?.message || err || "");
          if (msg.includes("already claimed")) {
            console.info("[match-host] claim lost; using elected host", { matchId });
            return;
          }
          console.warn("[match-host] claim failed", err);
        });
      }
    } catch (err) {
      console.warn("[match-host] claim failed", err);
    }
  }

  private _onMatchHostRow(row: any, kind: "insert" | "update" | "delete"): void {
    if (!row) return;
    const matchId = String(row.matchId ?? row.match_id ?? "");
    if (!matchId) return;
    if (kind === "delete") {
      this._matchHostByMatch.delete(matchId);
      this._emit({
        type: "standalone_battle_role",
        match_id: matchId,
        role: "none",
        host_player_id: null,
      });
      // Tear down any standalone battle for this match.
      if (this._standaloneBattleMatchId === matchId) {
        this.closeStandaloneBattle("host_row_removed");
      }
      return;
    }
    const hostPlayerId = String(row.hostPlayerId ?? row.host_player_id ?? "");
    this._matchHostByMatch.set(matchId, hostPlayerId);
    const myPlayerId = this._playerId;
    const role = myPlayerId && hostPlayerId === myPlayerId ? "host" : "client";
    this._emit({
      type: "standalone_battle_role",
      match_id: matchId,
      role,
      host_player_id: hostPlayerId,
    });
    this._maybeStartStandaloneBattle(matchId);
  }

  private _onStandaloneStartPacketRow(
    row: any,
    kind: "upsert" | "delete",
  ): void {
    if (!row) return;
    const matchId = String(row.matchId ?? row.match_id ?? "");
    if (!matchId) return;
    if (kind === "delete") {
      this._standaloneStartPacketByMatch.delete(matchId);
      return;
    }
    const json = String(row.startPacketJson ?? row.start_packet_json ?? "");
    if (!json) return;
    this._standaloneStartPacketByMatch.set(matchId, json);
    this._maybeStartStandaloneBattle(matchId);
  }

  /** Phase 5d — once we know our role *and* (if host) we have the start
   *  packet, bring up the embedded battle via `connectStandaloneBattle`.
   *  Idempotent: `connectStandaloneBattle` no-ops if the same match is
   *  already attached. */
  private _maybeStartStandaloneBattle(matchId: string): void {
    if (!isStandaloneBattleEnabled()) return;
    if (!matchId) return;
    const match = this.conn ? this.conn.db.matches.id.find(matchId) : null;
    if (match && match.mode === "arena") {
      // Arena matches are hosted on the server, not via Tauri local/remote standalone battle.
      return;
    }
    const hostPlayerId = this._matchHostByMatch.get(matchId);
    if (!hostPlayerId) return;
    const myPlayerId = this._playerId;
    if (!myPlayerId) return;

    const relayUrl = this._buildStandaloneRelayUrl(matchId);
    if (!relayUrl) {
      console.warn("[standalone-battle] no relay URL available; skipping");
      return;
    }
    const jwt = this._readAuthJwt();
    if (!jwt) {
      console.warn("[standalone-battle] no auth JWT; standalone path requires login");
      return;
    }

    if (hostPlayerId === myPlayerId) {
      const json = this._standaloneStartPacketByMatch.get(matchId);
      if (!json) {
        // Wait for the view row to arrive.
        return;
      }
      let startPacket: any;
      try {
        startPacket = JSON.parse(json);
      } catch (err) {
        console.error("[standalone-battle] start packet parse failed", err);
        return;
      }
      const players = this._collectStandalonePlayers(matchId);
      this.connectStandaloneBattle(matchId, {
        kind: "host",
        relayUrl,
        jwt,
        startPacket,
        players,
        hostPlayerId: myPlayerId.toLowerCase(),
      });
    } else {
      this.connectStandaloneBattle(matchId, {
        kind: "client",
        relayUrl,
        jwt,
      });
    }
  }

  /** Map runtime config to the relay WebSocket URL. In production the relay
   *  lives behind nginx at `/api/relay/ws/<match_id>`; it may not share the
   *  direct SpacetimeDB host/port. */
  private _buildStandaloneRelayUrl(matchId: string): string | null {
    if (!matchId) return null;
    const configuredRelay = (window as any).WARGAME_RELAY_URL;
    if (typeof configuredRelay === "string" && configuredRelay.trim()) {
      const url = this._buildRelayUrlFromBase(configuredRelay.trim(), matchId);
      if (url) return url;
    }

    const configuredAuth = (window as any).WARGAME_AUTH_URL;
    if (typeof configuredAuth === "string" && configuredAuth.trim()) {
      const url = this._buildRelayUrlFromBase(configuredAuth.trim(), matchId);
      if (url) return url;
    }

    const stdbUrl = this._stdbUrl || "";
    const url = this._buildRelayUrlFromBase(stdbUrl, matchId);
    if (url) return url;

    if (typeof location !== "undefined" && location.host) {
      const scheme = location.protocol === "https:" ? "wss" : "ws";
      return `${scheme}://${location.host}/api/relay/ws/${matchId}`;
    }
    return null;
  }

  private _buildRelayUrlFromBase(baseUrl: string, matchId: string): string | null {
    if (!baseUrl) return null;
    let host = "";
    let secure = false;
    let path = "/api/relay/ws";
    try {
      const u = new URL(baseUrl);
      host = u.host;
      secure = u.protocol === "https:" || u.protocol === "wss:";
      const configuredPath = u.pathname.replace(/\/+$/, "");
      if (configuredPath && configuredPath !== "/") {
        path = configuredPath.endsWith("/api/relay/ws")
          ? configuredPath
          : `${configuredPath}/api/relay/ws`;
      }
    } catch {
      return null;
    }
    const scheme = secure ? "wss" : "ws";
    return `${scheme}://${host}${path}/${matchId}`;
  }

  private _readAuthJwt(): string | null {
    if (this._authToken) return this._authToken;
    try {
      const stored = localStorage.getItem("wargame.auth.token");
      return stored || null;
    } catch {
      return null;
    }
  }

  private _collectStandalonePlayers(
    matchId: string,
  ): Array<{ player_id: string; army_index: number }> {
    if (!this.conn) return [];
    // Map keyed by normalized player_id so we emit ONE frame per player.
    // The relay routes by JWT identity, lowercased — so we lowercase here
    // too, otherwise `to: Some("A")` won't match a client that joined as
    // "a". Duplicate rows (re-deploys, stale assignments, etc.) would
    // otherwise cause the worker to emit multiple frames per tick per
    // player, which the host's `if player_id == host_id` filter and the
    // relay's targeted route both match, producing a per-tick view swap.
    const seen = new Map<string, { player_id: string; army_index: number }>();
    for (const army of this.conn.db.match_armies.iter()) {
      const aMatchId = String((army as any).matchId ?? (army as any).match_id ?? "");
      if (aMatchId !== matchId) continue;
      const rawId = String(
        (army as any).playerId ?? (army as any).player_id ?? "",
      ).trim();
      if (!rawId) continue;
      const playerId = rawId.toLowerCase();
      const armyIndex = Number(
        (army as any).armyIndex ?? (army as any).army_index ?? 0,
      );
      const existing = seen.get(playerId);
      if (!existing) {
        seen.set(playerId, { player_id: playerId, army_index: armyIndex });
      } else if (existing.army_index !== armyIndex) {
        console.warn(
          "[standalone-battle] duplicate match_armies rows for player",
          playerId,
          "armies:",
          existing.army_index,
          armyIndex,
          "— keeping first",
        );
      }
    }
    return Array.from(seen.values());
  }

  private _openWorkerWs(
    matchId: string,
    endpointUrl: string,
    token: string,
  ): void {
    if (!this._playerId) {
      console.warn("[worker-ws] cannot open: no player id yet");
      return;
    }
    this._workerWsMatchId = matchId;
    this._workerWsEndpointUrl = endpointUrl;
    this._workerWsToken = token;
    this._workerBridge.attach(matchId);
    const lastTick = this._lastEmittedTick ?? 0;
    const ws = new WorkerWsClient(
      endpointUrl,
      {
        matchId,
        playerId: this._playerId,
        token,
        lastSeenTick: lastTick > 0 ? lastTick : null,
      },
      {
        onOpen: () => {
          console.info("[worker-ws] connected", { matchId, endpointUrl });
          this._flushWorkerOrderQueue();
          this._emit({
            type: "worker_ws_status",
            status: "connected",
            match_id: matchId,
          });
        },
        onMatchStart: (info) => {
          console.info("[worker-ws] match_start", info);
          this._emit({
            type: "worker_ws_status",
            status: "match_start",
            match_id: info.match_id,
            tick_hz: info.tick_hz,
          });
        },
        onTickFrame: (frame: WorkerTickFrame) => {
          // Phase 5c: feed the row cache and re-emit the unified `tick`
          // event from the SpacetimeDB-style `_emitTickUpdate` path so the
          // renderer doesn't need to know which pipeline produced the data.
          const accepted = this._workerBridge.setFrame(frame);
          if (!accepted) return;
          this._scheduleTick();
          this._emit({
            type: "worker_tick_frame",
            match_id: matchId,
            tick: frame.tick,
            phase: frame.phase,
            full: frame.full,
            units: frame.units,
            enemy_units: frame.enemy_units,
            unit_skills: frame.unit_skills,
            unit_order_queue: frame.unit_order_queue,
            unit_path_waypoints: frame.unit_path_waypoints,
            engagements: frame.engagements,
            projectiles: frame.projectiles,
            messengers: frame.messengers,
            capture_points: frame.capture_points,
            structure_state: frame.structure_state,
            camps: frame.camps,
            events: this._workerBridge.events,
            visibility: frame.visibility,
            watchtowers: frame.watchtowers,
          });
          this._scheduleTick();
        },
        onMatchEnd: (end) => {
          console.info("[worker-ws] match_end", end);
          this._emit({
            type: "worker_ws_status",
            status: "match_end",
            match_id: end.match_id,
            winner: end.winner,
          });
          this._closeWorkerWs("match_end");
        },
        onError: (code, message) => {
          console.warn("[worker-ws] error", code, message);
          this._emit({
            type: "worker_ws_status",
            status: "error",
            match_id: matchId,
            code,
            message,
          });
        },
        onClose: (info) => {
          console.info("[worker-ws] closed", info);
          this._emit({
            type: "worker_ws_status",
            status: "closed",
            match_id: matchId,
            code: info.code,
            reason: info.reason,
          });
          this._workerWs = null;
          // If the endpoint row is still present and we're still in the
          // running match, the orchestrator's heartbeat-respawn flow will
          // either re-publish the same URL or a new one — the row insert /
          // update handler will reopen automatically.
        },
      },
    );
    this._workerWs = ws;
    ws.connect();
  }

  /** Phase 5b — bring up a standalone Tauri battle (host or client). The
   *  caller (Phase 5c host election + lobby flow) is responsible for
   *  picking the role and supplying relay url + JWT + start packet.
   *  Returns immediately; status flows through the same `worker_ws_status`
   *  / `worker_tick_frame` events as the legacy worker-WS path so the
   *  renderer doesn't need to know which transport it's reading from. */
  connectStandaloneBattle(matchId: string, mode: StandaloneMode): void {
    if (!isStandaloneBattleEnabled()) {
      console.warn(
        "[standalone-battle] connect requested but not running in Tauri shell",
      );
      return;
    }
    if (!this._playerId) {
      console.warn("[standalone-battle] cannot open: no player id yet");
      return;
    }
    if (
      this._standaloneBattle &&
      this._standaloneBattle.isOpen &&
      this._standaloneBattleMatchId === matchId
    ) {
      return;
    }
    this.closeStandaloneBattle("rebind");
    // The legacy worker WS is mutually exclusive with the standalone
    // bridge — if any orchestrator endpoint was open, drop it first.
    this._closeWorkerWs("standalone_takeover");
    this._workerBridge.attach(matchId);
    this._standaloneBattleMatchId = matchId;

    const lastTick = this._lastEmittedTick ?? 0;
    const client = new StandaloneBattleClient(
      {
        matchId,
        playerId: this._playerId,
        token: "",
        lastSeenTick: lastTick > 0 ? lastTick : null,
      },
      mode,
      {
        onOpen: () => {
          console.info("[standalone-battle] connected", {
            matchId,
            mode: mode.kind,
          });
          this._emit({
            type: "worker_ws_status",
            status: "connected",
            match_id: matchId,
          });
        },
        onMatchStart: (info) => {
          this._emit({
            type: "worker_ws_status",
            status: "match_start",
            match_id: info.match_id,
            tick_hz: info.tick_hz,
          });
        },
        onTickFrame: (frame: WorkerTickFrame) => {
          const accepted = this._workerBridge.setFrame(frame);
          if (!accepted) return;
          this._scheduleTick();
          this._emit({
            type: "worker_tick_frame",
            match_id: matchId,
            tick: frame.tick,
            phase: frame.phase,
            full: frame.full,
            units: frame.units,
            enemy_units: frame.enemy_units,
            unit_skills: frame.unit_skills,
            unit_order_queue: frame.unit_order_queue,
            unit_path_waypoints: frame.unit_path_waypoints,
            engagements: frame.engagements,
            projectiles: frame.projectiles,
            messengers: frame.messengers,
            capture_points: frame.capture_points,
            structure_state: frame.structure_state,
            camps: frame.camps,
            events: this._workerBridge.events,
            visibility: frame.visibility,
            watchtowers: frame.watchtowers,
          });
          this._scheduleTick();
        },
        onMatchEnd: (end) => {
          console.info("[standalone-battle] match_end", end);
          this._emit({
            type: "worker_ws_status",
            status: "match_end",
            match_id: end.match_id,
            winner: end.winner,
          });
          // Standalone has no SpacetimeDB-side host writing
          // `match.phase = finished`, so the matches.onUpdate subscription
          // never fires. Push the results event ourselves before tearing
          // down so main.js can switch to the results screen.
          this._emitStandaloneMatchEnded(end);
          this.closeStandaloneBattle("match_end");
        },
        onError: (code, message) => {
          console.warn("[standalone-battle] error", code, message);
          this._emit({
            type: "worker_ws_status",
            status: "error",
            match_id: matchId,
            code,
            message,
          });
        },
        onClose: (info) => {
          console.info("[standalone-battle] closed", info);
          this._emit({
            type: "worker_ws_status",
            status: "closed",
            match_id: matchId,
            code: info.code,
            reason: info.reason,
          });
          this._standaloneBattle = null;
        },
      },
    );
    this._standaloneBattle = client;
    client.connect();
  }

  closeStandaloneBattle(reason: string = "client_close"): void {
    const client = this._standaloneBattle;
    this._standaloneBattle = null;
    this._standaloneBattleMatchId = null;
    if (!client) {
      this._workerBridge.detach();
      return;
    }
    try {
      client.close(reason);
    } catch {
      /* ignore */
    }
    this._workerBridge.detach();
  }

  private _closeWorkerWs(reason: string): void {
    if (!this._workerWs) {
      this._workerWsMatchId = null;
      this._workerWsEndpointUrl = null;
      this._workerWsToken = null;
      this._workerBridge.detach();
      return;
    }
    try {
      this._workerWs.close(reason);
    } catch {
      /* ignore */
    }
    this._workerWs = null;
    this._workerWsMatchId = null;
    this._workerWsEndpointUrl = null;
    this._workerWsToken = null;
    this._workerOrderQueue = [];
    this._workerBridge.detach();
  }

  /** Send an order envelope through the worker WebSocket. Returns true only
   *  when the order was actually accepted by an open worker WS; returns false
   *  when no worker is connected so callers fall back to the in-band
   *  SpacetimeDB reducer pipeline. */
  private _trySendWorkerOrder(action: string, payload: any): boolean {
    if (this._standaloneBattle && this._standaloneBattle.isOpen) {
      const wire = this._toWorkerOrderWire(action, payload);
      return this._standaloneBattle.sendOrder(wire.action, wire.payload);
    }
    if (this._workerWs && this._workerWs.isOpen) {
      const wire = this._toWorkerOrderWire(action, payload);
      return this._workerWs.sendOrder(wire.action, wire.payload);
    }
    return false;
  }

  /** Translate a reducer-shaped order (camelCase keys, reducer-style action
   *  name like ``dispatch_order``) into the wire form the worker's
   *  `wargame_simulation::orders::apply_orders` dispatcher expects.
   *
   *  Mirrors the in-module reducers (`reducers/orders.rs`) which:
   *    1. rename ``dispatch_order``→``dispatch`` and ``queue_orders``→``queue``;
   *    2. emit snake_case JSON payloads;
   *    3. for ``queue_orders``, parse the ``ordersJson`` string into an
   *       ``orders`` array. */
  private _toWorkerOrderWire(
    action: string,
    payload: any,
  ): { action: string; payload: any } {
    const actionMap: Record<string, string> = {
      dispatch_order: "dispatch",
      queue_orders: "queue",
    };
    const wireAction = actionMap[action] ?? action;
    const snake = this._camelKeysToSnake(payload);
    delete snake.match_id; // worker scopes by session, not payload
    if (action === "queue_orders" && typeof snake.orders_json === "string") {
      try {
        const parsed = JSON.parse(snake.orders_json);
        snake.orders = Array.isArray(parsed)
          ? parsed.map((o: any) => this._camelKeysToSnake(o))
          : [];
      } catch {
        snake.orders = [];
      }
      delete snake.orders_json;
    }
    if (action === "group_order" && typeof snake.unit_ids_json === "string") {
      try {
        snake.unit_ids = JSON.parse(snake.unit_ids_json);
      } catch {
        snake.unit_ids = [];
      }
      delete snake.unit_ids_json;
    }
    return { action: wireAction, payload: snake };
  }

  private _camelKeysToSnake(obj: any): any {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;
    const out: any = {};
    for (const [k, v] of Object.entries(obj)) {
      const sk = k.replace(/[A-Z]/g, (m) => "_" + m.toLowerCase());
      out[sk] = v;
    }
    return out;
  }

  private _flushWorkerOrderQueue(): void {
    if (!this._workerWs || !this._workerWs.isOpen) return;
    const pending = this._workerOrderQueue;
    if (!pending.length) return;
    this._workerOrderQueue = [];
    for (const { action, payload } of pending) {
      const wire = this._toWorkerOrderWire(action, payload);
      this._workerWs.sendOrder(wire.action, wire.payload);
    }
  }

  private _subscribeToMatch(matchId: string): Promise<void> {
    if (!this.conn) return Promise.reject(new Error("Not connected"));
    if (
      this._matchSubscription?.isActive &&
      this._matchSubscriptionId === matchId
    ) {
      return Promise.resolve();
    }

    this._clearMatchSubscription();
    const matchLiteral = this._sqlStringLiteral(matchId);
    return new Promise((resolve, reject) => {
      this._matchSubscriptionId = matchId;
      this._matchSubscription = this.conn!.subscriptionBuilder()
        .onApplied(() => resolve())
        .onError((ctx: any) => reject(ctx?.event?.error ?? ctx?.error ?? ctx))
        .subscribe([
          `SELECT * FROM matches WHERE id = ${matchLiteral}`,
          `SELECT * FROM match_armies WHERE match_id = ${matchLiteral}`,
          `SELECT * FROM match_capture_points WHERE match_id = ${matchLiteral}`,
          `SELECT * FROM match_wall_build_projects WHERE match_id = ${matchLiteral}`,
          `SELECT * FROM my_match_roads WHERE match_id = ${matchLiteral}`,
          `SELECT * FROM match_economy WHERE match_id = ${matchLiteral}`,
          `SELECT * FROM match_ant_groups WHERE match_id = ${matchLiteral}`,
          `SELECT * FROM match_terrain_overrides WHERE match_id = ${matchLiteral}`,
          `SELECT * FROM match_events WHERE match_id = ${matchLiteral}`,
          `SELECT * FROM my_match_meta`,
          `SELECT * FROM my_match_follow_waypoints`,
          `SELECT * FROM my_match_worker_endpoint`,
          `SELECT * FROM match_host WHERE match_id = ${matchLiteral}`,
          // Phase 5d — standalone host start packet (caller-filtered view).
          `SELECT * FROM my_standalone_start_packet`,
          // Legacy in-band battle-state subscriptions. Kept active until the
          // Python orchestrator subscribes to `match_handoff_requests` and a
          // worker actually drives the match. The C2 gate in `process_tick`
          // ensures the module stops writing these rows the moment a worker
          // registers, so they go quiet automatically and the worker bridge
          // takes over via `_workerBridge.isActive()` in the entity getters.
          `SELECT * FROM my_friendly_units`,
          `SELECT * FROM my_enemy_units`,
          `SELECT * FROM match_structure_state WHERE match_id = ${matchLiteral}`,
          `SELECT * FROM match_camps WHERE match_id = ${matchLiteral}`,
          `SELECT * FROM my_unit_skills`,
          `SELECT * FROM my_unit_order_queue`,
          `SELECT * FROM my_unit_path_waypoints`,
          `SELECT * FROM my_engagements`,
          `SELECT * FROM my_projectiles`,
          `SELECT * FROM my_messengers`,
          `SELECT * FROM my_visibility_bitmap`,
        ]);
    });
  }

  private _ensureMapCatalogLoaded(): Promise<
    { name: string; displayName: string }[]
  > {
    // Map catalog is sourced exclusively from the server `map_data` table.
    // Uploaded maps (via admin_gui -> register_map) are the single source of
    // truth. The base subscription already streams `map_data`, so in the
    // common path we just read from the cached table. We still await the
    // subscription readiness to avoid a race right after connect.
    if (!this.conn) return Promise.reject(new Error("Not connected"));

    const buildCatalog = () => {
      const rows = [...this.conn!.db.map_data.iter()];
      const byDisplayKey = new Map<string, { name: string; displayName: string }>();
      for (const row of rows) {
        if (row?.isActive === false) continue;
        const name = `${row?.name || ""}`.trim();
        if (!name) continue;
        let displayName = name;
        try {
          const parsed = JSON.parse(row?.mapJson || "{}");
          if (parsed && typeof parsed.name === "string" && parsed.name.trim()) {
            displayName = parsed.name.trim();
          }
        } catch {
          // Leave displayName as the registered name.
        }

        const displayKey = displayName.toLocaleLowerCase();
        const existing = byDisplayKey.get(displayKey);
        if (!existing || name.length < existing.name.length) {
          byDisplayKey.set(displayKey, { name, displayName });
        }
      }

      const entries = [...byDisplayKey.values()]
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
      this._mapCatalog = entries;
      this._mapCatalogLoaded = true;
      return entries;
    };

    if (this._subscriptionReady) {
      return Promise.resolve(buildCatalog());
    }

    if (this._mapCatalogLoadPromise) return this._mapCatalogLoadPromise;

    this._mapCatalogLoadPromise = new Promise((resolve, reject) => {
      const start = Date.now();
      const tick = () => {
        if (!this.conn) {
          this._mapCatalogLoadPromise = null;
          reject(new Error("Not connected"));
          return;
        }
        if (this._subscriptionReady) {
          this._mapCatalogLoadPromise = null;
          resolve(buildCatalog());
          return;
        }
        if (Date.now() - start > 10_000) {
          this._mapCatalogLoadPromise = null;
          reject(new Error("Timed out waiting for map_data subscription"));
          return;
        }
        setTimeout(tick, 50);
      };
      tick();
    });
    return this._mapCatalogLoadPromise;
  }

  private _ensureServerMapEntryLoaded(mapName: string): Promise<void> {
    if (!this.conn) return Promise.reject(new Error("Not connected"));

    const normalized = `${mapName || ""}`.trim();
    if (!normalized) return Promise.resolve();
    if (this._loadedServerMapEntries.has(normalized)) return Promise.resolve();
    if (this.conn.db.map_data.name.find(normalized)) {
      this._loadedServerMapEntries.add(normalized);
      return Promise.resolve();
    }

    if (this._serverMapSubscriptions.has(normalized)) {
      const existing = this._serverMapSubscriptions.get(normalized)!;
      if (existing.isActive) {
        return Promise.resolve();
      }
    }

    const query = `SELECT * FROM map_data WHERE name = ${this._sqlStringLiteral(normalized)}`;
    return new Promise((resolve, reject) => {
      const handle = this.conn!.subscriptionBuilder()
        .onApplied(() => {
          this._loadedServerMapEntries.add(normalized);
          this._serverMapSubscriptions.set(normalized, handle);
          resolve();
        })
        .onError((ctx: any) => {
          const error = ctx?.event?.error ?? ctx?.error ?? ctx;
          this._serverMapSubscriptions.delete(normalized);
          reject(error);
        })
        .subscribe([query]);
      this._serverMapSubscriptions.set(normalized, handle);
    });
  }

  private _findPendingCreatedLobbyMatch(): any | null {
    const lobbyMatches = this._currentLobbyMatches();
    return (
      lobbyMatches.find(
        (row: any) =>
          row.hostPlayerId === this._playerId &&
          !this._pendingCreatedMatchIds.has(row.matchId),
      ) || null
    );
  }

  private _maybeEmitPendingCreatedMatch() {
    if (!this._awaitingCreatedMatch) return;
    const row = this._findPendingCreatedLobbyMatch();
    if (!row) return;

    this._awaitingCreatedMatch = false;
    this._pendingCreatedMatchIds.add(row.matchId);
    this._stopLobbySubscription();
    this._matchId = row.matchId;
    void this._subscribeToMatch(row.matchId)
      .then(async () => {
        const mapData = await this._loadMapData(row.mapName);
        this._mapData = mapData;
        this._emit({
          type: "match_created",
          match_id: row.matchId,
          map: mapData,
        });
      })
      .catch((error: any) => {
        this._emitReducerError(
          error,
          "Failed to subscribe to the created match",
        );
      });
  }

  private _createClientId(prefix: string): string {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return `${prefix}_${crypto.randomUUID()}`;
    }
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  // --- Send (translates old WS message format to SpacetimeDB reducer calls) ---

  send(data: any) {
    if (!this.conn) return;

    switch (data.type) {
      case "authenticate":
        // SpacetimeDB uses identity-based auth; check if we have a user
        this._checkAuth();
        break;

      case "set_name":
        this.conn.reducers.setName({ name: data.name });
        break;

      case "create_match": {
        const mapName = data.map_name || "default";
        const numTeams = data.num_teams || 2;
        const armyBudget = data.army_budget || 150;
        const maxArmySize = data.max_army_size || 15;
        const ruleset = data.ruleset || "classical";
        const season = data.season || "summer";
        const parsedStartTimeHour = Number(data.start_time_hour);
        const startTimeHour = Number.isFinite(parsedStartTimeHour)
          ? parsedStartTimeHour
          : 5;
        void this._ensureLobbySubscription()
          .then(() => {
            this._pendingCreatedMatchIds = new Set(
              this._currentLobbyMatches().map((row: any) => row.matchId),
            );
            this._awaitingCreatedMatch = true;
            return this.conn!.reducers.createMatch({
              mapName,
              numTeams,
              armyBudget,
              maxArmySize,
              ruleset,
              season,
              startTimeHour,
            });
          })
          .then(() => {
            this._maybeEmitPendingCreatedMatch();
          })
          .catch((error: any) => {
            this._awaitingCreatedMatch = false;
            this._emitReducerError(error, "Failed to create match");
          });
        break;
      }

      case "register_map": {
        const name = data.name || data.map_name;
        const mapJson =
          typeof data.map_json === "string"
            ? data.map_json
            : typeof data.mapJson === "string"
              ? data.mapJson
              : JSON.stringify(data.map || {});
        if (!name) {
          this._emit({ type: "error", message: "Missing map name" });
          break;
        }
        void this.registerMapData(name, mapJson)
          .then(() => this._emit({ type: "map_registered", name }))
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to register map");
          });
        break;
      }

      case "join_match": {
        const matchId = data.match_id;
        this._matchId = matchId;
        this._lastEmittedTick = null;
        this._stopLobbySubscription();
        void this._subscribeToMatch(matchId)
          .then(async () => {
            const match = this.conn!.db.matches.id.find(matchId);
            if (!match) {
              this._finishedConquestMatchIds.add(matchId);
              this._matchId = null;
              this._lastEmittedTick = null;
              this._clearMatchSubscription();
              this._emitConquestState();
              return;
            }
            if (match.phase === "finished") {
              this._emitMatchEnded(match);
              return;
            }
            let alreadyJoined = false;
            let slotIndex = 0;
            for (const a of this.conn!.db.match_armies.iter()) {
              if (a.matchId !== matchId) continue;
              if (a.playerId === this._playerId) {
                alreadyJoined = true;
                slotIndex = a.armyIndex;
                break;
              }
              if (!a.playerId) {
                slotIndex = a.armyIndex;
                break;
              }
            }

            if (!alreadyJoined && match?.phase === "lobby") {
              await this.conn!.reducers.joinMatch({
                matchId,
                armyIndex: slotIndex,
              });
            }
            await this._emitMatchJoined(matchId);
          })
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to join match");
          });
        break;
      }

      case "leave_match":
        if (this._matchId) {
          void this.conn.reducers
            .leaveMatch({ matchId: this._matchId })
            .catch((error: any) => {
              this._emitReducerError(error, "Failed to leave match");
            });
          this._clearMatchSubscription();
          this._matchId = null;
        }
        break;

      case "detach_match":
        if (this._matchId) {
          this._clearMatchSubscription();
          this._matchId = null;
          this._emitActiveMatchAvailable();
        }
        break;

      case "resume_match": {
        const matchId = data.match_id || this._matchId;
        if (matchId) {
          void this._restoreActiveMatchContext(matchId).catch((error: any) => {
            this._emitReducerError(error, "Failed to rejoin match");
          });
        }
        break;
      }

      case "concede_match":
        if (this._matchId) {
          const accepted = this._trySendWorkerOrder("concede", {
            matchId: this._matchId,
          });
          if (!accepted) {
            console.warn("concede_match ignored: no battle worker is connected");
          }
          return accepted;
        }
        return false;
        break;

      case "claim_army":
        if (this._matchId) {
          // Leave current slot first, then join new one
          this.conn.reducers.leaveMatch({ matchId: this._matchId });
          this.conn.reducers.joinMatch({
            matchId: this._matchId,
            armyIndex: data.army_index,
          });
        }
        break;

      case "add_bot":
        if (this._matchId) {
          const matchId = this._matchId;
          void this.conn.reducers
            .addBotToMatch({ matchId, armyIndex: data.army_index })
            .catch((error: any) => {
              this._emitReducerError(error, "Failed to add bot");
            });
        }
        break;

      case "ready":
        if (this._matchId) {
          this.conn.reducers.ready({ matchId: this._matchId });
        }
        break;

      case "select_roster_and_ready":
        if (this._matchId) {
          const matchId = this._matchId;
          const units = data.units || [];
          const unitsJson = JSON.stringify(units);
          void this.conn.reducers
            .selectRoster({
              matchId,
              unitsJson,
            })
            .then(() => this.conn!.reducers.ready({ matchId }))
            .then(() => {
              this._emit({
                type: "roster_selected",
                units,
                army_index: this._getMyArmyIndex(),
              });
            })
            .catch((error: any) => {
              this._emitReducerError(error, "Failed to ready roster");
            });
        }
        break;

      case "start_match":
        if (this._matchId) {
          void (this.conn.reducers.startMatch({ matchId: this._matchId }) as any)
            ?.catch?.((error: any) => {
              this._emitReducerError(error, "Failed to start match");
            });
        }
        break;

      case "set_player_color":
        if (this._matchId && data.color) {
          this.conn.reducers.setArmyColor({
            matchId: this._matchId,
            color: data.color,
          });
        }
        break;

      case "set_player_culture":
        if (this._matchId && data.culture) {
          (this.conn.reducers as any).setArmyCulture({
            matchId: this._matchId,
            culture: data.culture,
          });
        }
        break;

      case "set_conquest_culture":
        if (data.ruleset && data.culture) {
          (this.conn.reducers as any).setConquestCulture({
            ruleset: data.ruleset,
            culture: data.culture,
          });
        }
        break;

      case "get_armies":
        this._emitArmiesList();
        break;

      case "get_formations":
        this._emitFormationsList(data.army_id);
        break;

      case "get_available_maps": {
        void this._ensureMapCatalogLoaded()
          .then((maps) => {
            this._emit({
              type: "available_maps",
              maps: maps || [],
            });
          })
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to load available maps");
          });
        break;
      }

      case "join_campaign_world":
      case "join_campaign":
        void this.conn.reducers
          .joinCampaignWorld({})
          .then(() => this._emitCampaignState())
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to join campaign");
          });
        break;

      case "get_campaign_state":
        this._emitCampaignState();
        break;

      case "join_conquest": {
        const ruleset = data.ruleset || "classical";
        void this.conn.reducers
          .joinConquest({ ruleset })
          .then(() => this._emitConquestState())
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to join conquest");
          });
        break;
      }

      case "get_conquest_state":
        this._emitConquestState();
        break;

      case "choose_conquest_starter": {
        const ruleset = data.ruleset || "classical";
        const territoryId = data.territory_id ?? data.territoryId;
        if (!territoryId) {
          this._emit({ type: "error", message: "Missing conquest territory" });
          break;
        }
        void this.conn.reducers
          .chooseConquestStarter({ ruleset, territoryId })
          .then(() => this._emitConquestState())
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to choose conquest starter");
          });
        break;
      }

      case "join_conquest_queue": {
        const ruleset = data.ruleset || "classical";
        const territoryId = data.territory_id ?? data.territoryId;
        if (!territoryId) {
          this._emit({ type: "error", message: "Missing conquest territory" });
          break;
        }
        void this.conn.reducers
          .joinConquestQueue({ ruleset, territoryId })
          .then(() => this._emitConquestState())
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to join conquest queue");
          });
        break;
      }

      case "leave_conquest_queue": {
        const ruleset = data.ruleset || "classical";
        void this.conn.reducers
          .leaveConquestQueue({ ruleset })
          .then(() => this._emitConquestState())
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to leave conquest queue");
          });
        break;
      }

      case "enter_arena_queue": {
        const generalArchetype = data.general_archetype;
        const unitArchetypes = data.unit_archetypes || [];
        void this.conn.reducers.enterArenaQueue({
          generalArchetype,
          unitArchetypes,
        }).catch((error: any) => {
          this._emitReducerError(error, "Failed to enter arena queue");
        });
        break;
      }

      case "leave_arena_queue": {
        void this.conn.reducers.leaveArenaQueue({}).catch((error: any) => {
          this._emitReducerError(error, "Failed to leave arena queue");
        });
        break;
      }

      case "set_conquest_army_marker": {
        const ruleset = data.ruleset || "classical";
        const territoryId = data.territory_id ?? data.territoryId;
        if (!territoryId) {
          this._emit({ type: "error", message: "Missing conquest territory" });
          break;
        }
        void this.conn.reducers
          .setConquestArmyMarker({ ruleset, territoryId })
          .then(() => this._emitConquestState())
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to move conquest army marker");
          });
        break;
      }

      case "set_conquest_army_banner": {
        const ruleset = data.ruleset || "classical";
        const bannerId = data.banner_id ?? data.bannerId ?? "crown";
        const armyColor = data.army_color ?? data.armyColor ?? "#1565c0";
        void this.conn.reducers
          .setConquestArmyBanner({ ruleset, bannerId, armyColor })
          .then(() => this._emitConquestState())
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to update conquest army banner");
          });
        break;
      }

      case "save_conquest_battle_units": {
        const matchId = data.match_id || data.matchId;
        const unitIds = data.unit_ids || data.unitIds || [];
        void (this.conn.reducers as any)
          .saveConquestBattleUnits({ matchId, unitIds })
          .then(() => this._emitConquestState())
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to save conquest battle units");
          });
        break;
      }

      case "save_conquest_custom_unit_template": {
        void (this.conn.reducers as any)
          .updateConquestCustomUnitTemplate({
            templateId: data.template_id ?? data.templateId ?? data.id,
            displayName: data.display_name ?? data.displayName ?? "Levy",
            meleeWeapon: data.melee_weapon ?? data.meleeWeapon ?? "club",
            shield: data.shield ?? "none",
            armor: data.armor ?? "none",
            rangedWeapon: data.ranged_weapon ?? data.rangedWeapon ?? "none",
            mount: data.mount ?? "none",
            weaponTrainingRank: data.weapon_training_rank ?? data.weaponTrainingRank ?? 0,
            shieldTrainingRank: data.shield_training_rank ?? data.shieldTrainingRank ?? 0,
            formationTrainingRank: data.formation_training_rank ?? data.formationTrainingRank ?? 0,
            enduranceTrainingRank: data.endurance_training_rank ?? data.enduranceTrainingRank ?? 0,
            skirmishingTrainingRank: data.skirmishing_training_rank ?? data.skirmishingTrainingRank ?? 0,
            horsemanshipTrainingRank: data.horsemanship_training_rank ?? data.horsemanshipTrainingRank ?? 0,
            fieldcraftTrainingRank: data.fieldcraft_training_rank ?? data.fieldcraftTrainingRank ?? 0,
          })
          .then(() => this._emitConquestState())
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to save custom unit template");
          });
        break;
      }

      case "create_conquest_custom_unit_template": {
        const ruleset = data.ruleset || "classical";
        void (this.conn.reducers as any)
          .createConquestCustomUnitTemplate({
            ruleset,
            displayName: data.display_name ?? data.displayName ?? "New Custom Unit",
          })
          .then(() => this._emitConquestState())
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to create custom unit template");
          });
        break;
      }

      case "delete_conquest_custom_unit_template": {
        const templateId = data.template_id ?? data.templateId ?? data.id;
        void (this.conn.reducers as any)
          .deleteConquestCustomUnitTemplate({ templateId })
          .then(() => this._emitConquestState())
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to delete custom unit template");
          });
        break;
      }

      case "reset_conquest_custom_unit_template": {
        const ruleset = data.ruleset || "classical";
        void (this.conn.reducers as any)
          .resetConquestCustomUnitTemplate({
            ruleset,
            culture: data.culture ?? "",
          })
          .then(() => this._emitConquestState())
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to reset custom unit template");
          });
        break;
      }

      case "set_campaign_army_destination":
      case "campaign_move_army":
        void this.conn.reducers
          .setCampaignArmyDestination({
            destinationX: data.destination_x ?? data.x,
            destinationY: data.destination_y ?? data.y,
          })
          .then(() => this._emitCampaignState())
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to move campaign army");
          });
        break;

      case "recruit_campaign_units":
      case "campaign_recruit":
        void this.conn.reducers
          .recruitCampaignUnits({
            cityId: data.city_id,
            archetype: data.archetype,
            count: data.count || 1,
          })
          .then(() => this._emitCampaignState())
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to recruit campaign units");
          });
        break;

      case "claim_recruited_units":
      case "campaign_claim_recruits":
        void this.conn.reducers
          .claimRecruitedUnits({ queueRowId: data.queue_row_id ?? data.row_id })
          .then(() => this._emitCampaignState())
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to claim recruited units");
          });
        break;

      case "get_map_data": {
        const name = data.map_name || "default";
        this._loadMapData(name).then((mapData) => {
          this._emit({ type: "map_data", map: mapData });
        });
        break;
      }

      case "list_matches":
        void this._ensureLobbySubscription()
          .then(() => this._emitMatchList())
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to load match list");
          });
        break;

      case "stop_list_matches":
        this._stopLobbySubscription();
        break;

      case "select_roster":
        if (this._matchId) {
          const units = data.units || [];
          const unitsJson = JSON.stringify(units);
          this.conn.reducers.selectRoster({
            matchId: this._matchId,
            unitsJson,
          });
          // Emit roster_selected locally so the UI updates immediately
          this._emit({
            type: "roster_selected",
            units,
            army_index: this._getMyArmyIndex(),
          });
        }
        break;

      case "deploy_units":
        if (this._matchId) {
          const deploymentsJson = JSON.stringify(data.deployments || []);
          this.conn.reducers.deployUnits({
            matchId: this._matchId,
            deploymentsJson,
          });
        }
        break;

      // --- Order commands ---
      case "order_move":
      case "order_attack":
      case "order_halt":
      case "order_brace":
      case "order_stance":
      case "order_fire_at":
      case "order_artillery":
      case "order_ranged_stance":
      case "order_guard":
      case "order_aggressive":
      case "order_toggle_fire_at_will":
      case "order_garrison":
      case "order_ungarrison":
      case "order_attack_structure":
      case "order_place_camp":
      case "order_attack_camp":
      case "order_toggle_builder":
      case "order_build_wall":
      case "order_build_road":
        if (this._matchId) {
          const orderPayload = {
            matchId: this._matchId,
            targetUnitId: data.unit_id || data.target_unit_id || "",
            orderType: data.type.replace("order_", ""),
            targetX: data.target_x ?? null,
            targetY: data.target_y ?? null,
            facing: data.facing ?? null,
            stance: data.slot ?? data.stance ?? null,
            rangedStance: data.ranged_stance ?? null,
            radius: data.radius ?? null,
            targetAttackUnitId: data.target_unit_id ?? null,
          };
          const workerPayload = data.type === "order_artillery"
            ? { ...orderPayload, slot: data.slot ?? data.stance ?? null } : orderPayload;
          if (!this._trySendWorkerOrder("dispatch_order", workerPayload)) {
            this.conn.reducers.dispatchOrder(orderPayload);
          }
        }
        break;

      case "order_queue":
        if (this._matchId) {
          const queuePayload = {
            matchId: this._matchId,
            targetUnitId: data.unit_id || "",
            ordersJson: JSON.stringify(data.orders || []),
          };
          if (!this._trySendWorkerOrder("queue_orders", queuePayload)) {
            this.conn.reducers.queueOrders(queuePayload);
          }
        }
        break;

      case "order_follow":
        if (this._matchId) {
          const marchOrderJson = JSON.stringify(data.march_order || []);
          const followPayload = {
            matchId: this._matchId,
            marchOrderJson,
          };
          if (!this._trySendWorkerOrder("order_follow", followPayload)) {
            this.conn.reducers.orderFollow(followPayload);
          }
        }
        break;

      case "group_order":
        if (this._matchId) {
          const groupPayload = {
            matchId: this._matchId,
            unitIdsJson: JSON.stringify(data.unit_ids || []),
            orderType: data.order_type,
          };
          if (!this._trySendWorkerOrder("group_order", groupPayload)) {
            this.conn.reducers.groupOrder(groupPayload);
          }
        }
        break;

      case "toggle_gate":
        if (this._matchId) {
          const gatePayload = {
            matchId: this._matchId,
            gateIndex: data.gate_index >>> 0,
          };
          if (!this._trySendWorkerOrder("toggle_gate", gatePayload)) {
            this.conn.reducers.toggleGate(gatePayload);
          }
        }
        break;

      case "designate_harvest_tile":
        if (this._matchId) {
          console.log("[harvest] sending designateHarvestTile", { matchId: this._matchId, tileX: data.tile_x, tileY: data.tile_y });
          const harvestPayload = {
            matchId: this._matchId,
            tileX: data.tile_x >>> 0,
            tileY: data.tile_y >>> 0,
          };
          if (!this._trySendWorkerOrder("designate_harvest_tile", harvestPayload)) {
            void (this.conn.reducers.designateHarvestTile(harvestPayload) as any);
          }
        } else {
          console.warn("[harvest] designate_harvest_tile dropped: no matchId");
        }
        break;

      case "undesignate_harvest_tile":
        if (this._matchId) {
          const undesignatePayload = {
            matchId: this._matchId,
            tileX: data.tile_x >>> 0,
            tileY: data.tile_y >>> 0,
          };
          if (!this._trySendWorkerOrder("undesignate_harvest_tile", undesignatePayload)) {
            void (this.conn.reducers.undesignateHarvestTile(undesignatePayload) as any);
          }
        }
        break;

      case "cancel_wall_build_project":
        if (this._matchId) {
          const cancelPayload = {
            matchId: this._matchId,
            projectId: String(data.project_id || ""),
          };
          if (!this._trySendWorkerOrder("cancel_wall_build_project", cancelPayload)) {
            void (this.conn.reducers.cancelWallBuildProject(cancelPayload) as any);
          }
        }
        break;

      case "set_group":
        this.conn.reducers.setGroup({
          armyId: data.army_id,
          groupNumber: data.group_number,
          name: data.name || "",
          unitIdsJson: JSON.stringify(data.unit_ids || []),
        });
        break;

      case "delete_group":
        this.conn.reducers.deleteGroup({
          armyId: data.army_id,
          groupNumber: data.group_number,
        });
        break;

      case "get_groups":
        this._emitGroupsList(data.army_id);
        break;

      case "change_map":
        // Map changing not yet implemented in SpacetimeDB module
        break;

      case "save_formation": {
        const armyId = data.army_id;
        const name = (data.name || "").trim();
        const slots = Array.isArray(data.slots) ? data.slots : [];
        if (!armyId || !name || slots.length === 0) {
          this._emit({ type: "error", message: "Invalid formation data" });
          break;
        }

        const formationId = this._createClientId("formation");
        void this.conn.reducers
          .saveFormation({
            formationId,
            armyId,
            name,
            slotsJson: JSON.stringify(slots),
          })
          .then(() => {
            this._emit({
              type: "formation_saved",
              formation_id: formationId,
              army_id: armyId,
              name,
              slots,
            });
          })
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to save formation");
          });
        break;
      }

      case "update_formation": {
        const formationId = data.formation_id;
        const name = (data.name || "").trim();
        const slots = Array.isArray(data.slots) ? data.slots : [];
        if (!formationId || !name || slots.length === 0) {
          this._emit({ type: "error", message: "Invalid formation data" });
          break;
        }

        void this.conn.reducers
          .updateFormation({
            formationId,
            name,
            slotsJson: JSON.stringify(slots),
          })
          .then(() => {
            this._emit({
              type: "formation_updated",
              formation_id: formationId,
            });
            const formation = [
              ...(this.conn?.db.my_army_formations.iter() ?? []),
            ].find((f) => f.id === formationId);
            if (formation) {
              this._emitFormationsList(formation.armyId);
            }
          })
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to update formation");
          });
        break;
      }

      case "delete_formation": {
        const formationId = data.formation_id;
        if (!formationId) {
          this._emit({ type: "error", message: "Invalid formation data" });
          break;
        }

        void this.conn.reducers
          .deleteFormation({ formationId })
          .then(() => {
            this._emit({
              type: "formation_deleted",
              formation_id: formationId,
            });
          })
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to delete formation");
          });
        break;
      }

      case "save_march_order": {
        const armyId = data.army_id;
        const marchOrder = Array.isArray(data.march_order)
          ? data.march_order
          : [];
        if (!armyId) {
          this._emit({ type: "error", message: "Invalid march order data" });
          break;
        }

        void this.conn.reducers
          .saveMarchOrder({
            armyId,
            marchOrderJson: JSON.stringify(marchOrder),
          })
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to save march order");
          });
        break;
      }

      case "form_up": {
        if (!this._matchId) break;
        const slots = Array.isArray(data.slots) ? data.slots : [];
        if (slots.length === 0) {
          this._emit({ type: "error", message: "No formation slots provided" });
          break;
        }

        void this.conn.reducers
          .formUp({
            matchId: this._matchId,
            slotsJson: JSON.stringify(slots),
          })
          .then(() => {
            this._emit({
              type: "formation_dispatched",
              dispatched_count: slots.length,
            });
          })
          .catch((error: any) => {
            this._emitReducerError(error, "Failed to apply formation");
          });
        break;
      }

      case "create_army":
      case "delete_army":
        console.warn("[Network] Command not yet implemented:", data.type);
        break;

      default:
        console.warn("[Network] Unknown message type:", data.type);
    }
  }

  private _mapCache: Record<string, any> = {};

  private _normalizeWallSections(
    structures: any[],
    cellSize: number,
    terrainCols: number,
    terrainRows: number,
  ): any[] {
    if (!Array.isArray(structures) || !Number.isFinite(cellSize) || cellSize <= 0) {
      return Array.isArray(structures) ? structures : [];
    }

    const normalized: any[] = [];
    for (const structure of structures) {
      if (structure?.type !== "wall") {
        normalized.push(structure);
        continue;
      }

      const width = Math.max(1, Number(structure.width ?? cellSize));
      const height = Math.max(1, Number(structure.height ?? cellSize));
      const startCol = Math.floor(Number(structure.x || 0) / cellSize);
      const startRow = Math.floor(Number(structure.y || 0) / cellSize);
      const endCol = Math.max(
        startCol + 1,
        Math.ceil((Number(structure.x || 0) + width) / cellSize),
      );
      const endRow = Math.max(
        startRow + 1,
        Math.ceil((Number(structure.y || 0) + height) / cellSize),
      );

      for (let row = startRow; row < endRow; row++) {
        for (let col = startCol; col < endCol; col++) {
          if (row < 0 || col < 0 || row >= terrainRows || col >= terrainCols) {
            continue;
          }
          normalized.push({
            ...structure,
            x: col * cellSize,
            y: row * cellSize,
            width: cellSize,
            height: cellSize,
          });
        }
      }
    }

    return normalized;
  }

  private _normalizeMapData(mapName: string, raw: any): any {
    const tg = raw.terrain_grid || {};
    const structures = this._normalizeWallSections(
      raw.structures || [],
      Number(tg.cell_size ?? 100),
      Number(tg.cols ?? 0),
      Number(tg.rows ?? 0),
    );
    if (
      Array.isArray(tg.chunks) &&
      tg.chunks.length > 0 &&
      tg.chunks.some((chunk: any) => !!chunk?.file)
    ) {
      return this._createChunkedMapRuntime(mapName, raw);
    }

    if (isProceduralManifest(raw)) {
      const mapData: any = createProceduralMapRuntime(mapName, raw);
      mapData.structures = this._normalizeWallSections(
        mapData.structures || [],
        Number(mapData.cell_size ?? tg.cell_size ?? 100),
        Number(mapData.terrain_cols ?? tg.cols ?? 0),
        Number(mapData.terrain_rows ?? tg.rows ?? 0),
      );
      return mapData;
    }

    if (Array.isArray(tg.chunks) && tg.chunks.length > 0) {
      return this._createChunkedMapRuntime(mapName, raw);
    }

    const height_cells = tg.height_cells || (tg.cells || []).map((t: number) => {
      if (t === 2) return 1; // Hills
      if (t === 9) return 2; // Mountains
      if (t === 10) return 3; // High Mountains
      return 0;
    });

    return {
      name: raw.name || mapName,
      is_siege: !!raw.is_siege,
      is_arena: !!raw.is_arena,
      width: raw.width,
      height: raw.height,
      teams: raw.teams || [{ name: "Team 1" }, { name: "Team 2" }],
      spawn_zones: raw.spawn_zones || [],
      structures,
      terrain_grid: raw.terrain_grid,
      cell_size: tg.cell_size,
      terrain_cols: tg.cols,
      terrain_rows: tg.rows,
      terrain_cells: tg.cells || [],
      height_cells: height_cells,
      road_graph: raw.road_graph || null,
    };
  }

  private _loadMapDataFromServer(mapName: string): any | null {
    if (!this.conn) return null;

    let row = this.conn.db.map_data.name.find(mapName);
    if (!row) {
      row =
        [...this.conn.db.map_data.iter()].find((candidate: any) => {
          try {
            const parsed = JSON.parse(candidate.mapJson);
            return parsed?.name === mapName;
          } catch {
            return false;
          }
        }) || null;
    }
    if (!row?.mapJson) return null;

    try {
      const parsed = JSON.parse(row.mapJson);
      return this._normalizeMapData(mapName, parsed);
    } catch (error) {
      console.warn(
        "[Network] Failed to parse server map data:",
        mapName,
        error,
      );
      return null;
    }
  }

  private _createChunkedMapRuntime(mapName: string, manifest: any): any {
    const tg = manifest.terrain_grid || {};
    const chunkCols = tg.chunk_cols || 0;
    const chunkRows = tg.chunk_rows || 0;
    const chunkCellCols = tg.chunk_cell_cols || 0;
    const chunkCellRows = tg.chunk_cell_rows || 0;
    const chunks = Array.isArray(tg.chunks) ? tg.chunks : [];
    const chunkIndex = new Map<string, any>();
    for (const chunk of chunks) {
      chunkIndex.set(`${chunk.x},${chunk.y}`, chunk);
    }

    const loadedChunks = new Map<string, any>();
    const pendingLoads = new Map<string, Promise<any>>();
    const baseUrl = new URL(`maps/${mapName}/`, window.location.href);

    const mapData: any = {
      name: manifest.name || mapName,
      is_siege: !!manifest.is_siege,
      is_arena: !!manifest.is_arena,
      width: manifest.width,
      height: manifest.height,
      teams: manifest.teams || [{ name: "Team 1" }, { name: "Team 2" }],
      spawn_zones: manifest.spawn_zones || [],
      structures: this._normalizeWallSections(
        manifest.structures || [],
        Number(tg.cell_size ?? 100),
        Number(tg.cols ?? 0),
        Number(tg.rows ?? 0),
      ),
      terrain_grid: manifest.terrain_grid,
      generator: manifest.generator || null,
      overlays: manifest.overlays || null,
      editor_mode: manifest.editor_mode || null,
      editor_version: manifest.editor_version || null,
      cell_size: tg.cell_size,
      terrain_cols: tg.cols,
      terrain_rows: tg.rows,
      terrain_cells: [],
      height_cells: [],
      chunked: true,
      road_graph: manifest.road_graph || null,
      terrain_chunk_cols: chunkCols,
      terrain_chunk_rows: chunkRows,
      terrain_chunk_cell_cols: chunkCellCols,
      terrain_chunk_cell_rows: chunkCellRows,
      terrain_chunks: chunks,
      getChunkMeta: (cx: number, cy: number) =>
        chunkIndex.get(`${cx},${cy}`) || null,
      getLoadedChunk: (cx: number, cy: number) =>
        loadedChunks.get(`${cx},${cy}`) || null,
      loadChunk: async (cx: number, cy: number) => {
        const key = `${cx},${cy}`;
        const existing = loadedChunks.get(key);
        if (existing) return existing;
        if (pendingLoads.has(key)) return pendingLoads.get(key);

        const meta = chunkIndex.get(key);
        if (!meta?.file) return null;

        const request = fetch(new URL(meta.file, baseUrl).toString())
          .then(async (resp) => {
            if (!resp.ok) {
              throw new Error(
                `Failed to load terrain chunk ${key}: ${resp.status} ${resp.statusText}`,
              );
            }
            const chunk = await resp.json();
            loadedChunks.set(key, chunk);
            pendingLoads.delete(key);
            return chunk;
          })
          .catch((error) => {
            pendingLoads.delete(key);
            console.warn(
              "[Network] Failed to load terrain chunk:",
              mapName,
              key,
              error,
            );
            return null;
          });

        pendingLoads.set(key, request);
        return request;
      },
    };

    return mapData;
  }

  private async _loadChunkedMapData(mapName: string): Promise<any | null> {
    try {
      const manifestUrl = new URL(
        `maps/${mapName}/manifest.json`,
        window.location.href,
      );
      const resp = await fetch(manifestUrl.toString());
      if (!resp.ok) {
        return null;
      }
      const manifest = await resp.json();
      return this._normalizeMapData(mapName, manifest);
    } catch (error) {
      console.warn(
        "[Network] Failed to load chunked map manifest:",
        mapName,
        error,
      );
      return null;
    }
  }

  private async _loadMapData(mapName: string): Promise<any> {
    if (this._mapCache[mapName]) return this._mapCache[mapName];

    // Server `map_data` is the single source of truth for map definitions.
    // We always try the uploaded server row first and only fall back to
    // bundled client assets if the map is not registered on the server.
    try {
      await this._ensureServerMapEntryLoaded(mapName);
    } catch (error) {
      console.warn(
        "[Network] Failed to subscribe to server map entry:",
        mapName,
        error,
      );
    }
    const serverMap = this._loadMapDataFromServer(mapName);
    if (serverMap) {
      this._mapCache[mapName] = serverMap;
      return serverMap;
    }

    console.warn(
      "[Network] Map not registered on server; falling back to bundled assets:",
      mapName,
    );

    const chunkedMap = await this._loadChunkedMapData(mapName);
    if (chunkedMap) {
      this._mapCache[mapName] = chunkedMap;
      return chunkedMap;
    }
    try {
      const mapUrl = new URL(`maps/${mapName}.json`, window.location.href);
      const resp = await fetch(mapUrl.toString());
      if (resp.ok) {
        const json = await resp.json();
        const mapData = this._normalizeMapData(mapName, json);
        this._mapCache[mapName] = mapData;
        return mapData;
      }
      console.warn(
        "[Network] Failed to load map response:",
        mapName,
        resp.status,
        resp.statusText,
      );
    } catch (e) {
      console.warn("[Network] Failed to load map:", mapName, e);
    }
    // Fallback
    const fallbackMap = {
      name: mapName,
      is_siege: false,
      is_arena: false,
      width: 3000,
      height: 2000,
      teams: [{ name: "Team 1" }, { name: "Team 2" }],
      spawn_zones: [],
      structures: [],
      terrain_grid: { cell_size: 100, cols: 0, rows: 0, cells: [] },
      cell_size: 100,
      terrain_cols: 0,
      terrain_rows: 0,
      terrain_cells: [],
      height_cells: [],
    };
    this._mapCache[mapName] = fallbackMap;
    return fallbackMap;
  }

  async registerMapData(name: string, mapData: any): Promise<void> {
    if (!this.conn) throw new Error("Not connected");
    await this._waitForConnectionReady();

    const mapJson = typeof mapData === "string" ? mapData : JSON.stringify(mapData);
    JSON.parse(mapJson);
    await this.conn.reducers.registerMap({ name, mapJson });

    delete this._mapCache[name];
    this._loadedServerMapEntries.delete(name);
    this._emit({ type: "map_registered", name });
    this._emit({ type: "available_maps", maps: await this._ensureMapCatalogLoaded() });
  }

  private _checkAuth() {
    const session = this._getSession();
    this._emit({
      type: "authenticated",
      authenticated: session?.authenticated ?? false,
      username: session?.username ?? null,
      tier: session?.tier ?? "free",
    });
  }

  private _emitArmiesList() {
    if (!this.conn) return;
    const armies: any[] = [];
    for (const a of this.conn.db.my_armies.iter()) {
      // Find units for this army
      const units: any[] = [];
      for (const u of this.conn.db.my_army_units.iter()) {
        if (u.armyId === a.id) {
          units.push({
            id: u.id,
            archetype: u.archetype,
            custom_name: u.customName,
            slot_index: u.slotIndex,
            is_general: u.isGeneral,
            level: u.level,
            experience: u.experience,
            troops_percent: u.troopsPercent,
            kills: u.kills,
            battles_survived: u.battlesSurvived,
          });
        }
      }
      armies.push({
        id: a.id,
        name: a.name,
        units,
      });
    }
    this._emit({ type: "armies_list", armies });
  }

  private _emitFormationsList(armyId: string) {
    if (!this.conn) return;
    const formations: any[] = [];
    for (const f of this.conn.db.my_army_formations.iter()) {
      if (f.armyId === armyId) {
        formations.push({
          id: f.id,
          name: f.name,
          slots: JSON.parse(f.slots || "[]"),
        });
      }
    }
    this._emit({ type: "formations_list", formations });
  }

  private _emitGroupsList(armyId: string) {
    if (!this.conn) return;
    const groups: any[] = [];
    for (const g of this.conn.db.my_army_groups.iter()) {
      if (g.armyId === armyId) {
        groups.push({
          id: g.id,
          group_number: g.groupNumber,
          name: g.name,
          unit_ids: JSON.parse(g.unitIds || "[]"),
        });
      }
    }
    this._emit({ type: "groups_list", groups });
  }

  private _emitMatchList() {
    if (!this.conn) return;
    const matches: any[] = [];
    for (const row of this.conn.db.skirmish_lobby_matches.iter()) {
      let playerNames: string[] = [];
      try {
        playerNames = JSON.parse(row.playerNames || "[]");
      } catch {
        playerNames = [];
      }
      matches.push({
        id: row.matchId,
        phase: row.phase,
        map_name: row.mapName,
        host: row.hostPlayerName || row.hostPlayerId,
        players: playerNames.map((name) => ({ name })),
      });
    }
    this._emit({ type: "match_list", matches });
  }

  private _getMyArmyIndex(): number {
    const found = this._lookupMyArmyIndex();
    return found ?? 0;
  }

  /** Same lookup as `_getMyArmyIndex` but returns null when the assignment
   *  isn't visible yet (instead of silently defaulting to army 0, which
   *  would mis-filter frames for the player who actually owns army 1). */
  private _lookupMyArmyIndex(): number | null {
    if (!this.conn || !this._matchId || !this._playerId) return null;
    const myMatch = this._matchId;
    const myPid = this._playerId.toLowerCase();
    for (const a of this.conn.db.match_armies.iter()) {
      if (
        a.matchId === myMatch &&
        String(a.playerId ?? "").toLowerCase() === myPid
      ) {
        return a.armyIndex;
      }
    }
    return null;
  }

  get connected(): boolean {
    return this.conn !== null;
  }

  // --- Auth methods (token issued by standalone auth service) ---

  async register(
    username: string,
    email: string,
    password: string,
  ): Promise<any> {
    if (!this.conn) throw new Error("Not connected");
    await this._waitForConnectionReady();
    await this._authFetch("/api/auth/register", {
      username,
      email,
      password,
    });
    return this.login(username, password);
  }

  async login(username: string, password: string): Promise<any> {
    if (!this.conn) throw new Error("Not connected");
    await this._waitForConnectionReady();
    const identityHex = this._identity?.toHexString?.() || this._playerId;
    const result = await this._authFetch("/api/auth/login", {
      username,
      password,
      identity_hex: identityHex,
    });
    return this._finalizeLogin(result);
  }

  /**
   * Drive the Patreon OAuth flow and exchange the resulting pending token
   * for a real, identity-bound session JWT.
   *
   * Web build: open a popup pointed at /api/auth/patreon/start?return=web
   * and wait for it to postMessage the callback payload back.
   * Tauri build: ask for the desktop authorize URL, open it via the system
   * browser, and wait for a deep-link callback delivered through the
   * `wargame://` URI scheme.
   */
  async loginWithPatreon(): Promise<any> {
    if (!this.conn) throw new Error("Not connected");
    await this._waitForConnectionReady();

    const isTauri = !!(globalThis as any)?.__TAURI_INTERNALS__;
    let payload: any;
    if (isTauri) {
      payload = await this._patreonFlowDesktop();
    } else {
      payload = await this._patreonFlowWeb();
    }
    if (!payload?.token) throw new Error("Patreon login was cancelled");

    const identityHex = this._identity?.toHexString?.() || this._playerId;
    const result = await this._authFetch("/api/auth/login_patreon", {
      token: payload.token,
      identity_hex: identityHex,
    });
    return this._finalizeLogin(result);
  }

  /**
   * Steam-only login for the desktop client: Steam issues a Web API ticket,
   * the auth server verifies it and auto-creates the account.
   */
  async loginWithSteam(): Promise<any> {
    if (!this.conn) throw new Error("Not connected");
    await this._waitForConnectionReady();
    const core = (globalThis as any).__TAURI__?.core;
    if (!core?.invoke) throw new Error("Steam login requires the desktop client");
    const ticket: string = await core.invoke("steam_web_ticket");
    const identityHex = this._identity?.toHexString?.() || this._playerId;
    const result = await this._authFetch("/api/auth/login_steam", {
      ticket,
      identity_hex: identityHex,
    });
    return this._finalizeLogin(result);
  }

  /** SpacetimeDB connection token; a hosting GM's world worker acts with this identity. */
  getSpacetimeToken(): string {
    try {
      return localStorage.getItem("spacetimedb_token") || "";
    } catch {
      return "";
    }
  }

  getConnection(): any {
    return this.conn;
  }

  private async _finalizeLogin(result: any): Promise<any> {
    const token: string = result.token;
    this._authToken = token;
    try {
      localStorage.setItem("wargame.auth.token", token);
    } catch {
      /* storage disabled */
    }
    void _saveStandaloneAuthToken(token);

    await this.conn!.reducers.presentAuthToken({ tokenStr: token });
    const session = await this._waitForSession(
      (current) => !!current?.authenticated && !!current?.userId,
    );

    return {
      user_id: session.userId || result.user_id,
      username: session.username || session.playerName || result.username,
      tier: session.tier || result.tier || "free",
    };
  }

  private _patreonFlowWeb(): Promise<any> {
    return new Promise((resolve, reject) => {
      const base = this._resolveAuthBaseUrl();
      const url = `${base}/api/auth/patreon/start?return=web`;
      const popup = window.open(
        url,
        "patreon-oauth",
        "width=540,height=720,menubar=no,toolbar=no",
      );
      if (!popup) {
        reject(new Error("Popup blocked — allow popups for this site"));
        return;
      }
      const onMessage = (ev: MessageEvent) => {
        const data: any = ev.data;
        if (!data || data.source !== "patreon-oauth") return;
        window.removeEventListener("message", onMessage);
        clearInterval(closedCheck);
        if (data.error) {
          reject(new Error(String(data.error)));
        } else {
          resolve(data.payload || {});
        }
        try {
          popup.close();
        } catch {
          /* noop */
        }
      };
      const closedCheck = setInterval(() => {
        if (popup.closed) {
          clearInterval(closedCheck);
          window.removeEventListener("message", onMessage);
          reject(new Error("Patreon window was closed"));
        }
      }, 600);
      window.addEventListener("message", onMessage);
    });
  }

  private async _patreonFlowDesktop(): Promise<any> {
    const base = this._resolveAuthBaseUrl();
    const startUrl = `${base}/api/auth/patreon/start?return=desktop`;
    const startResp = await fetch(startUrl, {
      headers: { Accept: "application/json" },
    });
    if (!startResp.ok) {
      throw new Error(`Patreon start failed (${startResp.status})`);
    }
    const startBody = await startResp.json();
    const authorizeUrl: string = startBody.authorize_url;
    if (!authorizeUrl) throw new Error("Missing authorize URL");

    const tauri = (globalThis as any).__TAURI__ || {};
    const core = tauri.core;
    const event = tauri.event;
    if (!core?.invoke || !event?.listen) {
      throw new Error(
        "Patreon login requires the desktop client (Tauri APIs unavailable).",
      );
    }
    // Open the Patreon authorize page in the OS default browser via our
    // Rust `open_external_url` command. We avoid the @tauri-apps/plugin-shell
    // JS package: bare-specifier dynamic imports don't resolve at runtime in
    // the webview, and the prior fallback path crashed with
    // "Failed to resolve module specifier '@tauri-apps/plugin-shell'".
    await core.invoke("open_external_url", { url: authorizeUrl });

    return new Promise((resolve, reject) => {
      let unlisten: (() => void) | null = null;
      const timeout = setTimeout(() => {
        unlisten?.();
        reject(new Error("Patreon login timed out"));
      }, 5 * 60 * 1000);

      const handlePayload = (raw: string) => {
        if (!raw || !raw.includes("patreon/callback")) return false;
        clearTimeout(timeout);
        unlisten?.();
        const frag = raw.split("#")[1] || raw.split("?")[1] || "";
        const params = new URLSearchParams(frag);
        if (params.get("error")) {
          reject(new Error(params.get("error") || "patreon error"));
          return true;
        }
        resolve({
          token: params.get("token"),
          user_id: params.get("user_id"),
          username: params.get("username"),
          tier: params.get("tier"),
        });
        return true;
      };

      // tauri-plugin-deep-link emits "deep-link://new-url" with a payload
      // that is either a string or string[] depending on platform/version.
      event
        .listen("deep-link://new-url", (e: { payload: unknown }) => {
          const p = e.payload;
          const list = Array.isArray(p) ? (p as string[]) : [String(p ?? "")];
          for (const u of list) {
            if (handlePayload(u)) return;
          }
        })
        .then((un: () => void) => {
          unlisten = un;
        })
        .catch((err: unknown) => {
          clearTimeout(timeout);
          reject(err instanceof Error ? err : new Error(String(err)));
        });
    });
  }

  logout() {
    this._authToken = null;
    try {
      localStorage.removeItem("wargame.auth.token");
    } catch {
      /* storage disabled */
    }
    void _clearStandaloneAuthToken();
    if (this.conn) {
      void this.conn.reducers.clearAuth({}).catch((error: any) => {
        console.warn("[Network] Failed to clear auth", error);
      });
    }
  }

  hasToken(): boolean {
    try {
      return !!localStorage.getItem("wargame.auth.token");
    } catch {
      return false;
    }
  }

  /// Replay a stored auth JWT after (re)connection so the user stays logged
  /// in across SpacetimeDB wipes. Safe to call when no token is stored.
  private async _replayStoredAuthToken(): Promise<void> {
    if (!this.conn) return;
    let stored: string | null = null;
    try {
      stored = this._authToken || localStorage.getItem("wargame.auth.token");
    } catch {
      stored = this._authToken;
    }
    if (!stored) return;
    void _saveStandaloneAuthToken(stored);
    try {
      await this.conn.reducers.presentAuthToken({ tokenStr: stored });
    } catch (err: any) {
      const msg = String(err?.message || err);
      if (/expired|invalid|identity mismatch/i.test(msg)) {
        try {
          localStorage.removeItem("wargame.auth.token");
        } catch {
          /* noop */
        }
        this._authToken = null;
        void _clearStandaloneAuthToken();
      } else {
        console.warn("[Network] presentAuthToken replay failed", err);
      }
    }
  }
}

// ---- Standalone (Tauri) auth-token bridge -------------------------------
//
// The Tauri Rust side persists this token to the app's local data dir and
// attaches it as `Authorization: Bearer <token>` to the updater request, so
// installed clients can fetch the auth-gated /download/*.exe artifacts.
// In the web build these helpers are silent no-ops.

function _tauriInvoke(): ((cmd: string, args?: any) => Promise<any>) | null {
  const w: any = typeof window === "undefined" ? null : window;
  if (!w) return null;
  const internals = w.__TAURI_INTERNALS__;
  if (internals && typeof internals.invoke === "function") {
    return (cmd, args) => internals.invoke(cmd, args);
  }
  if (w.__TAURI__ && typeof w.__TAURI__.invoke === "function") {
    return (cmd, args) => w.__TAURI__.invoke(cmd, args);
  }
  return null;
}

async function _saveStandaloneAuthToken(token: string): Promise<void> {
  const invoke = _tauriInvoke();
  if (!invoke) return;
  try {
    await invoke("save_auth_token", { token });
  } catch (err) {
    console.warn("[Network] save_auth_token failed", err);
  }
}

async function _clearStandaloneAuthToken(): Promise<void> {
  const invoke = _tauriInvoke();
  if (!invoke) return;
  try {
    await invoke("clear_auth_token");
  } catch (err) {
    console.warn("[Network] clear_auth_token failed", err);
  }
}

export { Network };
